import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { notasVentaApi } from '../api/notasVenta'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import ModalListaClientes from '../components/ModalListaClientes'
import ModalAltaCliente from '../components/ModalAltaCliente'
import ModalReciboPresea from '../components/ModalReciboPresea'
import ModalComprobantesPresea from '../components/ModalComprobantesPresea'
import api from '../api/client'
import { pagosApi, mediosPagoApi } from '../api/pagos'
import { remitosApi } from '../api/remitos'
import EstadoBadge from '../components/EstadoBadge'
import GiftCardsNVCard from '../components/GiftCardsNVCard'
import LinksPagoNVCard from '../components/LinksPagoNVCard'
import Modal from '../components/Modal'
import NcDevolucionModal from '../components/NcDevolucionModal'
import RefacturacionModal from '../components/RefacturacionModal'
import Boton from '../components/Boton'
import BotonesCobroWA from '../components/BotonesCobroWA'
import BotonEncuestaSatisfaccion from '../components/BotonEncuestaSatisfaccion'
import AvisoPedidoRepetido from '../components/AvisoPedidoRepetido'
import { copiarTexto } from '../utils/copiar'
import CampoFecha from '../components/CampoFecha'
import MoneyInput from '../components/MoneyInput'
import SelectorBancoPresea from '../components/SelectorBancoPresea'
import { TIPOVALOR } from '../api/bancos'
import { useAuth } from '../context/AuthContext'
import { fmtFecha, fmtFechaHora, fmtHoraDe, fmtMonto, parseMonto, hoyISO } from '../utils/fecha'
import { PROVINCIAS, esCapitalFederal, matchLocalidad, matchProvincia } from '../data/argentina'
import CampoLocalidad from '../components/CampoLocalidad'
import { etiquetaCliente } from '../utils/cliente'
import { descargarArchivo } from '../utils/descargar'
import AvisosCard from '../components/avisos/AvisosCard'
import NotaClienteBanner from '../components/NotaClienteBanner'
import { useConsultasDeposito, PedirStockDepositoModal, ConsultasDepositoCard } from '../components/StockDeposito'
import { consultasStockApi } from '../api/consultasStock'
import { colors, font, text, space, radius, sx } from '../design/tokens'

const CAMPOS_MEDIO = {
  tarjeta:      ['nro_cupon_cheque', 'cod_autorizacion', 'id_transaccion', 'cuotas', 'lote'],
  cheque:       ['nro_cupon_cheque'],
  transferencia:['id_transaccion'],
  mp:           ['id_transaccion', 'cod_autorizacion', 'lote'],
  efectivo:     [],
}
// Cada medio con su propia fecha (igual que PagoFormPage). La del recibo es
// HOY y la fija el backend; la del cheque es su fecha de pago y es obligatoria.
const FECHA_POR_MEDIO = { cheque: 'Fecha de pago del cheque', transferencia: 'Fecha de la transferencia', tarjeta: 'Fecha del cupón', mp: 'Fecha de la operación' }

// Tipovalor de BANCOS Presea según el tipo del MedioPago (igual que PagoFormPage).
const TIPOVALOR_POR_TIPO = {
  tarjeta:       TIPOVALOR.TARJETA,
  mp:            TIPOVALOR.TARJETA,
  cheque:        TIPOVALOR.CHEQUE,
  efectivo:      TIPOVALOR.EFECTIVO,
  transferencia: TIPOVALOR.TRANSFERENCIA,
}
// Tarjeta y cheque exigen N_BCO real (sino Presea rebota con ERR13).
const TIPOS_REQUIEREN_BANCO = new Set(['tarjeta', 'cheque'])
// Efectivo NO se muestra: va siempre EFECTIVO PESOS (n_bco=999) automático
// (regla Martin 19 jun — "saca caja del modal y del item recibos").
const TIPOS_PERMITEN_BANCO  = new Set(['tarjeta', 'cheque', 'transferencia', 'mp'])
const N_BCO_AUTO_POR_TIPO   = { efectivo: 999 }
// Medios de pasarela que llegan desde Magento y NO tienen N_BCO en BANCOS de
// Presea — el operador debe editar el cobro y poner el método real (VISA,
// MASTERCARD, etc.) antes de enviar a Presea. La cadena de comprobantes
// muestra ⚠ cuando detecta uno de estos.
const MEDIOS_PASARELA_REVISAR = new Set(['mobbex', 'sugapay'])

// Polling del chain Presea: tras enviar el paquete, la pantalla refresca
// sola hasta que aparecen factura → remito → recibo, o hay error, o pasa el
// timeout. Sin esto el operador necesitaba 3 refresh manuales para ver el
// resultado completo.
//
// Intervalo con BACKOFF: rápido al inicio (la factura puede llegar en 5s y
// queremos verla apenas aparece), lento al final (los pasos restantes tardan
// 5-12s cada uno, no tiene sentido pedirles cada 0.8s).
//   0-5s    → cada 800ms
//   5-20s   → cada 1500ms
//   20s+    → cada 3000ms
const POLLING_TIMEOUT_MS = 90000
function pollingIntervalo(transcurridoMs) {
  if (transcurridoMs < 5000) return 800
  if (transcurridoMs < 20000) return 1500
  return 3000
}

// Deriva los 3 pasos del JSON de la NV. Cada paso puede ser:
//   'pendiente'  — todavía no llegó
//   'listo'      — Presea respondió OK
//   'na'         — paso no aplica (ej: no hay cobro vinculado → no se espera recibo)
function derivarPasos(nv) {
  if (!nv) return { factura: 'pendiente', remito: 'pendiente', recibo: 'pendiente' }
  // sin_factura (SOLO REMITO): la factura no aplica a esta operación.
  const factura = nv.numero_presea ? 'listo' : (nv.sin_factura ? 'na' : 'pendiente')
  const remito  = nv.numero_remito_presea ? 'listo' : 'pendiente'
  // Recibo: solo se espera si hay algún Pago confirmado/acreditado vinculado a
  // la NV. Si no hay pagos vigentes (cobro contra entrega, devuelto, etc.) el
  // paso queda 'na' y no bloquea el "paquete completo".
  const pagosVigentes = (nv.pagos || []).filter(p => !['anulado', 'rechazado', 'devuelto'].includes(p.estado))
  const recibo = pagosVigentes.length === 0
    ? 'na'
    : (pagosVigentes.every(p => p.numero_presea) ? 'listo' : 'pendiente')
  return { factura, remito, recibo }
}

/**
 * Link de WhatsApp para un teléfono argentino. Mismo criterio que el portal del
 * fletero: se limpia todo lo que no sea dígito y se antepone el 54 si no está.
 */
function waHref(numero) {
  const limpio = String(numero).replace(/\D/g, '')

  return `https://wa.me/${limpio.startsWith('54') ? limpio : '54' + limpio}`
}

export default function NotaVentaDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, hasAnyRole, rolOperativoVentas } = useAuth()
  const [nv, setNv] = useState(null)
  // Consulta de stock a depósito: la respuesta vuelve a esta NV.
  const deposito = useConsultasDeposito(id)
  const [showPedirDeposito, setShowPedirDeposito] = useState(false)
  // Aviso efímero del botón de copiar (no hay toasts en esta pantalla).
  const [copiado, setCopiado] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [mensaje, setMensaje] = useState('')
  const [error, setError] = useState('')
  const [showEnviarModal, setShowEnviarModal] = useState(false)
  const [showNcModal, setShowNcModal] = useState(false)
  const [showRefacturar, setShowRefacturar] = useState(false)
  const [emitiendoRefactura, setEmitiendoRefactura] = useState(false)
  const [editarCliente, setEditarCliente] = useState(false)
  const [reciboPresea, setReciboPresea] = useState(false)
  const [comprobantesPresea, setComprobantesPresea] = useState(false)
  // La proforma tarda unos segundos (consulta fiscal + PDF): sin este estado
  // el botón no avisaba y el operador re-clickeaba (historial duplicado, 8 ago).
  const [emitiendoProforma, setEmitiendoProforma] = useState(false)
  const [reprocesando, setReprocesando] = useState(false)

  // ♻️ Reprocesar con número nuevo (Martin, 31 ago 2026): cuando Presea
  // rechaza SIEMPRE igual esta NV (NUM_COMPRO quemado por un registro
  // corrupto), el clon con número nuevo destraba. El backend hace toda la
  // secuencia: clon sin renglones de canje + original archivada + gift cards
  // re-canjeadas + cobros movidos.
  const reprocesar = async () => {
    if (!window.confirm(
      'Crea una COPIA de esta NV con número nuevo y deja la original archivada.\n\n'
      + 'Los canjes de gift card y los cobros imputados se mueven a la copia.\n\n'
      + 'Usalo cuando Presea rechaza SIEMPRE con el mismo error (número quemado). ¿Continuar?'
    )) return
    setReprocesando(true)
    try {
      const { data } = await api.post(`/notas-venta/${id}/reprocesar`)
      navigate(`/notas-venta/${data.id}`)
    } catch (e) {
      setError(e.response?.data?.errors ? Object.values(e.response.data.errors).flat().join(' · ') : (e.response?.data?.message || 'No se pudo reprocesar.'))
    } finally { setReprocesando(false) }
  }
  // Polling del chain Presea: arranca cuando el operador envía el paquete.
  // estado: 'idle' | 'corriendo' | 'completo' | 'error' | 'timeout'
  const [polling, setPolling] = useState({ estado: 'idle', inicio: 0, error: null })
  // Tomar el pedido (web entra sin vendedor) + hilo de mensajes libres.
  const [tomando, setTomando] = useState(false)
  const [msgTexto, setMsgTexto] = useState('')
  const [msgEnviando, setMsgEnviando] = useState(false)

  const tomarPedido = async () => {
    setTomando(true)
    try {
      await api.post(`/notas-venta/${id}/tomar`)
      await cargar({ silencioso: true })
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo tomar el pedido.')
    } finally {
      setTomando(false)
    }
  }

  const soltarPedido = async () => {
    setTomando(true)
    try {
      await api.post(`/notas-venta/${id}/soltar`)
      await cargar({ silencioso: true })
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo soltar el pedido.')
    } finally {
      setTomando(false)
    }
  }

  const enviarMensaje = async () => {
    const texto = msgTexto.trim()
    if (!texto) return
    setMsgEnviando(true)
    try {
      const { data } = await api.post(`/notas-venta/${id}/mensajes`, { texto })
      setNv(prev => ({ ...prev, mensajes: [...(prev.mensajes || []), data] }))
      setMsgTexto('')
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo agregar el mensaje.')
    } finally {
      setMsgEnviando(false)
    }
  }

  const cargar = (opts = {}) => {
    const silencioso = !!opts.silencioso
    if (!silencioso) setCargando(true)
    return notasVentaApi.obtener(id)
      .then(({ data }) => { setNv(data); return data })
      .catch(() => { if (!silencioso) navigate('/notas-venta') })
      .finally(() => { if (!silencioso) setCargando(false) })
  }

  useEffect(() => { cargar() }, [id])

  // Factura de Crédito Electrónica (20 oct 2026): lo dice ARCA (cliente
  // obligado + monto). Pedido aparte del detalle para no demorarlo si ARCA
  // tarda; la regla vive en FacturaCreditoService, acá solo se pinta.
  const [fce, setFce] = useState(null)
  useEffect(() => {
    if (!nv?.id) return
    let vivo = true
    api.get(`/notas-venta/${nv.id}/factura-credito`)
      .then(({ data }) => { if (vivo) setFce(data) })
      .catch(() => { if (vivo) setFce(null) })
    return () => { vivo = false }
  }, [nv?.id, nv?.estado, nv?.numero_presea])

  // Polling del chain Presea: refresca la NV cada POLLING_INTERVALO_MS hasta
  // que (a) los 3 pasos están listos, (b) algún paso fue rechazado y la NV
  // volvió a borrador, o (c) timeout. Mientras corre, el banner arriba de la
  // cadena muestra el avance en vivo.
  useEffect(() => {
    if (polling.estado !== 'corriendo') return
    let timer
    let cancelado = false
    const tick = async () => {
      if (cancelado) return
      const data = await cargar({ silencioso: true })
      if (cancelado) return
      if (!data) {
        timer = setTimeout(tick, pollingIntervalo(Date.now() - polling.inicio))
        return
      }
      const pasos = derivarPasos(data)
      // Solo recibo (NV ya en Presea): se espera el número de LOS recibos
      // recién cargados, no el paquete entero.
      const cierreOk = polling.pagoIds
        ? polling.pagoIds.every(pid => (data.pagos || []).find(p => p.id === pid)?.numero_presea)
        : (pasos.factura === 'listo' || pasos.factura === 'na')
          && pasos.remito === 'listo'
          && (pasos.recibo === 'listo' || pasos.recibo === 'na')
      if (cierreOk) {
        setPolling(p => ({ ...p, estado: 'completo', error: null }))
        return
      }
      // Si la NV volvió a borrador, el chain rebotó (regla TOTAL: sin factura,
      // todo se aborta). Mostramos el error y paramos el polling.
      if (data.estado === 'borrador') {
        setPolling(p => ({ ...p, estado: 'error', error: 'El chain rebotó — la NV volvió a borrador. Revisá el historial de estados.' }))
        return
      }
      const transcurrido = Date.now() - polling.inicio
      if (transcurrido > POLLING_TIMEOUT_MS) {
        setPolling(p => ({ ...p, estado: 'timeout', error: p.pagoIds
          ? 'Presea está tardando en devolver el número. El recibo ya está en la cola: el sistema lo reintenta solo y el número aparece en esta NV cuando llegue.'
          : 'El chain está tardando más de lo esperado — recargá manualmente.' }))
        return
      }
      // Backoff adaptativo: rápido al principio para detectar la factura apenas
      // llega, lento después para no spamear cuando los WS de Presea ya están
      // tardando varios segundos por paso.
      timer = setTimeout(tick, pollingIntervalo(transcurrido))
    }
    // Primer tick casi inmediato (200ms) para que la UI muestre avance sin
    // esperar el primer intervalo completo.
    timer = setTimeout(tick, 200)
    return () => { cancelado = true; clearTimeout(timer) }
  }, [polling.estado, polling.inicio, polling.pagoIds, id])

  // Cuando el polling cierra OK, autodescartar el banner verde después de 5s.
  useEffect(() => {
    if (polling.estado !== 'completo') return
    const t = setTimeout(() => setPolling({ estado: 'idle', inicio: 0, error: null }), 5000)
    return () => clearTimeout(t)
  }, [polling.estado])

  const handleEnvioCompleto = async (seleccion) => {
    setEnviando(true)
    setError('')
    setShowEnviarModal(false)
    const mensajes = []
    const reciboIds = []
    try {
      // Los recibos se crean ANTES de enviar la NV y nacen CONFIRMADOS: así ya
      // están vinculados cuando el chain (EnviarNVAPreseaJob::encolarCobrosPendientes)
      // los busca para mandarlos a Presea con el paquete. Cargarlos en el modal ES
      // la confirmación — no hay paso de confirmar aparte.
      if (seleccion.recibos && seleccion.recibos.length > 0) {
        for (const r of seleccion.recibos) {
          if (!r.medio_pago_id || !r.importe) continue
          // El modal ya arma valores[] cuando la fila tiene N_BCO real
          // (multi-valor con pago_valores). Sin valores el backend sigue el
          // flujo legacy del codigo_presea del MedioPago — sirve para
          // efectivo/transferencia/MP que ya andaban.
          const { data: pagoCreado } = await pagosApi.crear({
            cliente_id: nv.cliente.id,
            fecha: r.fecha,
            importe: parseMonto(r.importe),
            medio_pago_id: parseInt(r.medio_pago_id),
            nro_cupon_cheque: r.nro_cupon_cheque || null,
            cod_autorizacion: r.cod_autorizacion || null,
            id_transaccion: r.id_transaccion || null,
            cuotas: parseInt(r.cuotas) || 1,
            lote: r.lote || null,
            observaciones: `Recibo vinculado a NV ${nv.numero_lgr}`,
            nv_ids: [nv.id],
            // Camino nativo LGR: cargar el recibo en el modal ES la confirmación
            // → nace confirmado y viaja a Presea con la NV.
            confirmar: true,
            ...(r.valores ? { valores: r.valores } : {}),
          })
          reciboIds.push(pagoCreado.id)
        }
        mensajes.push(`${seleccion.recibos.length} recibo(s) registrado(s)`)
        // NV que YA está en Presea: el backend manda el recibo en el acto
        // (PagoService::enviarReciboPosterior). El banner muestra que se está
        // enviando hasta que vuelve con su número — el operador no queda a
        // ciegas (Martin, 6 oct 2026).
        if (!seleccion.nv && nv.numero_presea && reciboIds.length > 0) {
          setPolling({ estado: 'corriendo', inicio: Date.now(), error: null, pagoIds: reciboIds })
        }
      }
      if (seleccion.nv) {
        const { data: nvActualizada } = await notasVentaApi.enviar(id, seleccion.soloRemito ? { solo_remito: true } : undefined)
        setNv(nvActualizada)
        mensajes.push(seleccion.soloRemito
          ? 'NV enviada — SOLO REMITO (sin factura ni recibo)'
          : `NV procesada — FAC ${nvActualizada.comprobantes?.find(c => c.tipo === 'factura')?.numero_presea || ''}`)
        // El chain Presea (factura → remito → recibo) corre async después de
        // la respuesta. Arrancamos el polling para que la pantalla muestre el
        // avance en vivo y el operador no tenga que refrescar 3 veces.
        setPolling({ estado: 'corriendo', inicio: Date.now(), error: null })
      }
      if (seleccion.remito90) {
        const r = seleccion.remito90
        const partes = [
          r.receptor_nombre,
          // El piso va PEGADO a la calle: separado por coma se lee como si
          // fuera otra cosa ("... 1530, 3 B, Florida") y se pierde de vista.
          [r.calle, r.piso].filter(Boolean).join(' '),
          r.localidad,
          r.provincia,
          r.codigo_postal,
        ].filter(Boolean)
        const payload = {
          cliente_id: nv.cliente.id,
          nota_venta_id: nv.id,
          transporte: '90',
          fecha_emision: hoyISO(),
          observaciones_raw: partes.join(', '),
          articulos: nv.items.map(it => ({ articulo_id: it.articulo_id, cantidad: it.cantidad })),
          // Valor declarado opcional (sug #38): solo viaja si se cargó.
          ...(parseMonto(r.valor_declarado) > 0 ? { valor_declarado: parseMonto(r.valor_declarado) } : {}),
        }
        // Cuando NO se tildó "usar dirección de facturación", la dirección que
        // armó el operador es info propia de envío → se persiste en la NV para
        // que el remito impreso, la asignación de zona y el detalle la lean en
        // vez de caer a cliente.domicilio.
        if (!seleccion.usarDirFact) {
          payload.direccion_envio = {
            receptor_nombre: r.receptor_nombre || null,
            telefono:        r.telefono || null,
            calle:           r.calle || null,
            piso:            r.piso || null,
            localidad:       r.localidad || null,
            provincia:       r.provincia || null,
            codigo_postal:   r.codigo_postal || null,
          }
        }
        const { data: remito } = await remitosApi.crear(payload)
        // NV ya facturada sin remito (venta en pasos): el backend encola el
        // despacho 'solo remito' — el número R000xxx se pisa con el de Presea
        // cuando el WS responde.
        mensajes.push(nv.estado === 'procesado' && !nv.numero_remito_presea
          ? `Remito ${remito.numero_presea} creado — se está emitiendo en Presea`
          : `Remito ${remito.numero_presea} creado`)
        cargar()
      }
      if (seleccion.remitoParcial) {
        // Envío parcial: el remito sale ENTERO en Presea (T80) y se despacha
        // de a partes desde el detalle del remito («Despachar parte»). La
        // dirección se guarda en la NV igual que en un T90: con ella nace
        // precargada cada parte.
        const r = seleccion.remitoParcial
        const payload = {
          cliente_id: nv.cliente.id,
          nota_venta_id: nv.id,
          transporte: '80',
          envio_parcial: true,
          fecha_emision: hoyISO(),
          articulos: nv.items.map(it => ({ articulo_id: it.articulo_id, cantidad: it.cantidad })),
        }
        if (!seleccion.usarDirFact) {
          payload.direccion_envio = {
            receptor_nombre: r.receptor_nombre || null,
            telefono:        r.telefono || null,
            calle:           r.calle || null,
            piso:            r.piso || null,
            localidad:       r.localidad || null,
            provincia:       r.provincia || null,
            codigo_postal:   r.codigo_postal || null,
          }
        }
        const { data: remito } = await remitosApi.crear(payload)
        mensajes.push(`Remito ${remito.numero_presea} creado como envío parcial — despachá cada parte desde el remito`)
        cargar()
      }
      if (seleccion.remito80) {
        const { data: remito } = await remitosApi.crear({
          cliente_id: nv.cliente.id,
          nota_venta_id: nv.id,
          transporte: '80',
          fecha_emision: hoyISO(),
          articulos: nv.items.map(it => ({ articulo_id: it.articulo_id, cantidad: it.cantidad })),
        })
        mensajes.push(`Remito T80 ${remito.numero_presea} creado`)
        cargar()
      }
      if (seleccion.remitoVirtual) {
        const { data: remito } = await remitosApi.crear({
          cliente_id: nv.cliente.id,
          nota_venta_id: nv.id,
          transporte: '0',
          fecha_emision: hoyISO(),
          articulos: nv.items.map(it => ({ articulo_id: it.articulo_id, cantidad: it.cantidad })),
        })
        mensajes.push(`Remito virtual ${remito.numero_presea} creado (se entrega solo al procesarse en Presea)`)
        cargar()
      }
      if (seleccion.remitoRetira) {
        const { data: remito } = await remitosApi.crear({
          cliente_id: nv.cliente.id,
          nota_venta_id: nv.id,
          transporte: '90',
          retira: true,
          sucursal_retiro: seleccion.sucursalRetiro || null,
          fecha_emision: hoyISO(),
          articulos: nv.items.map(it => ({ articulo_id: it.articulo_id, cantidad: it.cantidad })),
        })
        mensajes.push(`Remito ${remito.numero_presea} creado — listo para retirar en depósito`)
        cargar()
      }
      if (mensajes.length > 0) {
        setMensaje(mensajes.join(' · ') + '.')
        cargar()
      }
    } catch (err) {
      // Mostrar los errores POR CAMPO (data.errors) y no solo el message genérico,
      // así un 422 dice "El campo X es obligatorio" en vez de algo opaco.
      const data = err.response?.data
      const detalle = data?.errors ? Object.values(data.errors).flat().join(' · ') : null
      setError(detalle || data?.message || 'Error al enviar.')
      // Recargar SIEMPRE tras un fallo: los recibos ya creados antes del error
      // quedan vinculados — sin esto, reabrir el modal mostraba "falta cobrar"
      // plata que ya estaba cobrada (y se tentaba cargarla dos veces) (sug #53).
      cargar()
    } finally { setEnviando(false) }
  }

  if (cargando) return <p style={{ color: '#64748b' }}>Cargando...</p>
  if (!nv) return null

  // hasAnyRole: basta que ALGUNO de los roles del usuario sea operativo.
  const rolOperativo = rolOperativoVentas()
  const puedeEditar = nv.es_editable && rolOperativo
  const puedeGestionarComprobantes = rolOperativo && nv.estado !== 'frenado'
  // Anulados/rechazados/devueltos no cuentan como cobro vigente.
  const sinCobrosVigentes = (nv.pagos || []).filter(p => !['anulado', 'rechazado', 'devuelto'].includes(p.estado)).length === 0

  const total = nv.items?.reduce((acc, it) => acc + parseFloat(it.precio_final) * it.cantidad, 0) * (1 - parseFloat(nv.descuento_gral) / 100)

  /**
   * Resumen del pedido para pegar en el chat de Ventas de WhatsApp cuando hay
   * que aclarar algo. Va SIN destinatario a propósito: se copia y cada uno lo
   * pega donde corresponde.
   *
   * Los asteriscos son las negritas de WhatsApp. El detalle del renglón es el
   * mismo que se ve en pantalla —descripción propia si la hay, si no la del
   * artículo facturable— para que el mensaje y la NV digan lo mismo.
   */
  const textoParaWhatsApp = () => {
    const l = []
    l.push(`*${nv.numero_lgr}*`)
    if (nv.numero_presea) l.push(`Factura ${nv.numero_presea}`)

    const cli = [nv.cliente?.nombre, nv.cliente?.cuit, nv.cliente?.telefono, nv.email_contacto || nv.cliente?.email]
      .filter(Boolean)
    if (cli.length) { l.push(''); l.push(`*Cliente:* ${cli.join(' · ')}`) }

    l.push('')
    l.push('*Productos:*')
    for (const it of nv.items || []) {
      const nombre = it.descripcion?.trim()
        || it.articulo?.factura_como?.descripcion
        || it.articulo?.descripcion
        || '(sin detalle)'
      const turno = it.turno ? ` · turno ${it.turno}` : ''
      // Mismo código que la tabla de la NV: el facturable (alias) o el del artículo.
      const codigo = it.articulo?.factura_como?.codigo_alfa || it.articulo?.codigo_alfa
      l.push(`• ${it.cantidad}× ${codigo ? `[${codigo}] ` : ''}${nombre}${turno} — $${fmtMonto(it.subtotal)}`)
    }

    l.push('')
    l.push(`*Total:* $${fmtMonto(nv.total || 0)}`)
    // Con la factura emitida manda el total con percepciones: es lo que el
    // cliente tiene que pagar, y es la diferencia que suele generar la consulta.
    if (nv.numero_presea && nv.monto_a_cobrar != null
        && Math.abs(Number(nv.monto_a_cobrar) - Number(nv.total || 0)) > 0.005) {
      l.push(`*Total con percepciones:* $${fmtMonto(nv.monto_a_cobrar)}`)
    }
    return l.join('\n')
  }

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <Boton variante="ghost" sm style={{ marginBottom: '0.25rem' }} onClick={() => navigate(-1)}>← Volver</Boton>
          <h2 style={styles.title}>{nv.numero_lgr}</h2>
          {/* Número del pedido web (sug #93), a la vista sin bajar hasta "Origen". */}
          {nv.origen !== 'lgr' && nv.origen_externo_id && (
            <span style={styles.presea} title="Número del pedido web">
              Pedido web {nv.origen_externo_id}
            </span>
          )}
          {nv.numero_nv_presea && (
            <span style={styles.presea} title="NV en Presea (equivalente de LGR-… del lado ERP)">
              NV Presea {nv.numero_nv_presea}
            </span>
          )}
          {nv.numero_presea && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                descargarArchivo(`/notas-venta/${id}/factura-fiscal`, `factura_${nv.numero_presea}.pdf`)
              }}
              style={{ ...styles.presea, ...styles.preseaLink }}
              title="Descargar factura fiscal"
            >
              Factura {nv.numero_presea}
            </a>
          )}
          {nv.numero_remito_presea && <span style={styles.presea}>Remito {nv.numero_remito_presea}</span>}
          {nv.cliente_presea && (
            <div style={nv.cliente_presea.difiere_del_lgr ? styles.cliPreseaDiff : styles.cliPrea} title="Cliente con que Presea facturó esta NV">
              <span style={{ opacity: 0.7 }}>Facturado en Presea a:</span>{' '}
              <strong>{nv.cliente_presea.nombre}</strong>{' '}
              <code style={styles.preseaCodInline}>({nv.cliente_presea.codigo})</code>
              {nv.cliente_presea.difiere_del_lgr && (
                <span style={styles.cliPreseaWarn}> · ≠ cliente LGR</span>
              )}
            </div>
          )}
        </div>
        <div style={styles.acciones}>
          <EstadoBadge estado={nv.estado} />
          {/* Sugerencia #76 (Diego): un cobro que no cubre el monto a cobrar se
              dice con números al lado del estado — la NV no está completa. */}
          {(nv.pago_estado === 'parcial' || ((nv.pagos || []).some(p => !['anulado', 'rechazado', 'devuelto'].includes(p.estado)) && Number(nv.saldo) > 0)) && (
            <span title="Lo cobrado no cubre el monto a cobrar" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', fontSize: 12, fontWeight: 600 }}>
              Pago parcial · cobrado ${fmtMonto(nv.pagado || 0)} de ${fmtMonto(nv.monto_a_cobrar ?? nv.total)} · falta ${fmtMonto(nv.saldo)}
            </span>
          )}
          {/* Sug #131: devolución de la factura vigente — se dice qué pasó. */}
          {nv.pago_estado === 'devuelta' && (
            <span title="Se devolvió la venta y se reintegró la plata: no se debe nada" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 999, background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#334155', fontSize: 12, fontWeight: 600 }}>
              Devuelta{(nv.devoluciones || []).length ? ` el ${fmtFecha(nv.devoluciones[nv.devoluciones.length - 1].fecha)} · ${(nv.devoluciones || []).map(d => `${d.formulario} ${d.numero}`).join(', ')}` : ''}
            </span>
          )}
          {nv.pago_estado === 'a_favor' && (
            <span title="Después de la devolución, lo cobrado supera lo que el cliente debe" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 999, background: '#f5f3ff', border: '1px solid #c4b5fd', color: '#5b21b6', fontSize: 12, fontWeight: 600 }}>
              A favor del cliente ${fmtMonto(Math.abs(Number(nv.saldo) || 0))}{(nv.devoluciones || []).length ? ` · ${(nv.devoluciones || []).map(d => `${d.formulario} ${d.numero}`).join(', ')}` : ''}
            </span>
          )}
          <Boton variante="secondary" onClick={() => descargarArchivo(`/notas-venta/${id}/imprimir`, `nota_venta_${nv.numero_lgr}.pdf`)}>⬇ PDF</Boton>
          {/* FACTURA PROFORMA (no fiscal, no viaja a Presea): percepciones
              calculadas con la condición fiscal real del cliente. Solo tiene
              sentido ANTES de facturar — con la factura emitida ya está el
              comprobante real. */}
          {nv.es_editable && (
            <Boton
              variante="secondary"
              disabled={emitiendoProforma}
              title="Factura proforma: documento comercial con IVA y percepciones calculados (no válido como factura, no viaja a Presea)"
              onClick={async () => {
                if (emitiendoProforma) return
                setEmitiendoProforma(true)
                try {
                  await descargarArchivo(`/notas-venta/${id}/proforma`, `proforma_${nv.numero_lgr}.pdf`)
                  cargar({ silencioso: true })
                } catch (err) {
                  // La respuesta de error viene como blob (la descarga pide
                  // blob) — extraer el JSON para mostrar el motivo real.
                  let msg = 'No se pudo emitir la proforma.'
                  try {
                    const t = await err.response?.data?.text?.()
                    if (t) {
                      const j = JSON.parse(t)
                      msg = j.errors ? Object.values(j.errors).flat().join(' · ') : (j.message || msg)
                    }
                  } catch { /* mensaje genérico */ }
                  setError(msg)
                } finally { setEmitiendoProforma(false) }
              }}
            >{emitiendoProforma ? '⏳ Generando…' : '🧾 Proforma'}</Boton>
          )}
          {/* Factura y remito hechos A MANO en Presea sobre esta nota (20 oct
              2026 — Factura de Crédito Electrónica, que el WS no emite). */}
          {nv.estado !== 'procesado' && nv.numero_nv_presea && !nv.numero_presea && !nv.archivado && (
            <Boton
              variante="secondary"
              title={`Traer la factura y el remito que se hicieron a mano en Presea sobre la nota ${nv.numero_nv_presea}`}
              onClick={() => setComprobantesPresea(true)}
            >📥 Traer comprobantes de Presea</Boton>
          )}
          {nv.es_editable && (
            <Boton
              variante="secondary"
              disabled={reprocesando}
              title="Clon con número nuevo: para cuando Presea rechaza siempre igual esta NV (número quemado). Mueve canjes de gift card y cobros a la copia; la original queda archivada."
              onClick={reprocesar}
            >{reprocesando ? '⏳ Clonando…' : '♻️ Reprocesar (nº nuevo)'}</Boton>
          )}
          {/* NC / Devolución (proceso 15 ago 2026): la emisión es del desktop
              de Presea; LGR valida el número contra DETALLE_COMPRO y recién
              con ese OK refleja (NV liberada, remito anulado, decisión del
              recibo). Con mercadería en la calle escala a administración. */}
          {nv.estado === 'procesado' && nv.numero_presea && (
            <Boton
              variante="secondary"
              title="Anular esta venta con una Nota de Crédito o Devolución emitida en Presea"
              onClick={() => setShowNcModal(true)}
            >🧾 NC / Devolución</Boton>
          )}
          {/* Encuesta de satisfacción (Sugerencia #111): con la venta facturada,
              el vendedor le manda al cliente el link de la encuesta de compra. */}
          {nv.estado === 'procesado' && <BotonEncuestaSatisfaccion nvId={nv.id} />}
          {/* Refacturación (16 oct 2026): pedido entregado que necesita otra
              factura — DEVOLUCIÓN en Presea sin tocar la entrega + factura
              nueva sin logística. */}
          {nv.estado === 'procesado' && nv.numero_presea && (
            <Boton
              variante="secondary"
              title="El cliente necesita otra factura y el pedido ya se entregó: devolución en Presea + factura nueva, sin tocar la entrega"
              onClick={() => setShowRefacturar(true)}
            >🔁 Refacturar</Boton>
          )}
          {puedeEditar && (
            <Boton variante="secondary" onClick={() => navigate(`/notas-venta/${id}/editar`)}>Editar</Boton>
          )}
          {puedeGestionarComprobantes && nv.estado !== 'frenado' && !nv.refacturacion?.abierta && (
            <Boton variante="primary" disabled={enviando} onClick={() => setShowEnviarModal(true)}>
              {enviando ? 'Procesando...' : nv.es_editable ? '📦 Enviar paquete a Presea' : '📋 Agregar comprobantes'}
            </Boton>
          )}
          {/* Reabrir: para las NVs ya frenadas en producción (Frenar/Eliminar
              fueron removidos del detalle 24/06/26 — pedido del usuario). */}
          {rolOperativo && nv.estado === 'frenado' && (
            <Boton variante="success" onClick={async () => {
              try {
                const { data } = await notasVentaApi.reabrir(id)
                setNv(data); setMensaje('NV reabierta como borrador.')
              } catch (err) {
                setError(err.response?.data?.message || 'Error al reabrir.')
              }
            }}>
              ↺ Reabrir
            </Boton>
          )}
        </div>
      </div>

      {/* Cartel del recupero: el ciclo cerró con algo que el vendedor tiene
          que HACER (socio activado / comprobante) — visible, no solo en el
          historial (Martin, 8 sep: "¿pero en la NV????"). */}
      {/* Refacturación: lo que falta (factura nueva) y la cadena de cada vuelta. */}
      {nv.refacturacion?.abierta && (
        <div style={{ margin: '0 0 1rem', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: '0.9rem' }}>
          <div style={{ fontWeight: 600 }}>🔁 Refacturación en curso — falta la factura nueva</div>
          <div style={{ marginTop: 4, lineHeight: 1.5 }}>
            La devolución {nv.refacturacion.vueltas.at(-1)?.devolucion} ya está verificada. Corregí lo que haga falta (cliente, condición fiscal) y emití la factura nueva:
            sale con su remito, sin logística, y el cobro queda en esta venta.
          </div>
          {rolOperativo && (
            <div style={{ marginTop: 8 }}>
              <Boton variante="primary" sm disabled={emitiendoRefactura} onClick={async () => {
                setEmitiendoRefactura(true); setError('')
                try {
                  await notasVentaApi.enviar(id, {})
                  setMensaje('Factura nueva enviada a Presea. En unos segundos queda facturada.')
                  cargar({ silencioso: true })
                } catch (err) {
                  const errs = err.response?.data?.errors
                  setError(errs ? Object.values(errs).flat().join(' · ') : (err.response?.data?.message || 'No se pudo enviar la factura nueva.'))
                } finally { setEmitiendoRefactura(false) }
              }}>{emitiendoRefactura ? '⏳ Enviando…' : '🧾 Emitir factura nueva'}</Boton>
            </div>
          )}
        </div>
      )}
      {(nv.refacturacion?.vueltas?.length ?? 0) > 0 && !nv.refacturacion.abierta && (
        <div style={{ margin: '0 0 1rem', padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#334155', fontSize: '0.85rem', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 600 }}>🔁 Venta refacturada</div>
          {nv.refacturacion.vueltas.map(v => (
            <div key={v.id}>
              Factura {v.factura_original} → {v.devolucion} → factura {v.factura_nueva || '—'}
              {' · '}{v.motivo}{v.usuario ? ` · ${v.usuario}` : ''}{v.refacturada_at ? ` · ${fmtFecha(v.refacturada_at)}` : ''}
            </div>
          ))}
          {nv.refacturacion.diferencia && (
            <div style={{ marginTop: 4, color: '#92400e', fontWeight: 500 }}>
              {nv.refacturacion.diferencia.importe > 0
                ? `Falta cobrar ${fmtMonto(nv.refacturacion.diferencia.importe)}`
                : `Hay que devolverle al cliente ${fmtMonto(-nv.refacturacion.diferencia.importe)}`}
              {' '}(factura nueva {fmtMonto(nv.refacturacion.diferencia.facturado)}, cobrado {fmtMonto(nv.refacturacion.diferencia.cobrado)}).
            </div>
          )}
        </div>
      )}

      {fce?.facturada && fce.es_fce && (
        <div style={styles.fceEmitida}>🧾 <b>Factura de Crédito Electrónica.</b> El cliente tiene que aceptarla en ARCA y el cobro sigue su plazo.</div>
      )}
      {fce && !fce.facturada && fce.aplica && (
        <div style={styles.fceAviso}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>🧾 Va con Factura de Crédito Electrónica</div>
          <div>{fce.mensaje}</div>
          <div style={{ marginTop: 6 }}>
            {nv.numero_nv_presea
              ? <>La nota <span style={{ fontFamily: font.mono }}>{nv.numero_nv_presea}</span> ya está en Presea. {fce.instruccion}</>
              : <>Al enviar el paquete, LGR crea la nota en Presea y se frena antes de facturar. Después: {fce.instruccion.charAt(0).toLowerCase() + fce.instruccion.slice(1)}</>}
          </div>
        </div>
      )}

      {nv.aviso_recupero && (
        <div style={{ margin: '0 0 1rem', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, color: '#166534', fontSize: '0.9rem', fontWeight: 500 }}>
          {nv.aviso_recupero}
        </div>
      )}
      {/* NV RETENIDA (gift card heredada de Magento, Martin 26 sep 2026): el
          cliente canjeó una de las cards viejas; la NV espera la aprobación
          del super admin por Telegram antes de seguir su circuito. */}
      {/* POSIBLE FRAUDE (Martin, 13 oct 2026): solo marca, no frena. Los
          motivos los calcula RiesgoPagoService (IP vs zona de entrega, tarjeta
          con rechazos en otros pedidos); decide el vendedor (🚫 Fraude). */}
      {nv.riesgo_motivos?.length > 0 && (
        <div style={{ margin: '0 0 1rem', padding: '12px 16px', background: colors.errBg, border: `1px solid ${colors.errBorder}`, borderRadius: 8, color: colors.err, fontSize: '0.9rem' }}>
          <strong>⚠ Posible fraude</strong> — revisá antes de confirmar el cobro.
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {nv.riesgo_motivos.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {/* Membresía del Wine Club + producto restringido por fraude pagado por
          Mobbex (Martin, 29 oct 2026): la tienda lo deja pasar y lo resuelve
          el vendedor. El texto lo arma PagoRestringidoService. */}
      {nv.pago_restringido_aviso && (
        <div style={{ margin: '0 0 1rem', padding: '12px 16px', background: colors.warnBg, border: `1px solid ${colors.warnStrong}`, borderRadius: 8, color: colors.warn, fontSize: '0.9rem', fontWeight: 500 }}>
          🔒 {nv.pago_restringido_aviso}
        </div>
      )}
      <AvisoPedidoRepetido repetidos={nv.pedidos_repetidos} />
      {nv.retenida && (
        <div style={{ margin: '0 0 1rem', padding: '12px 16px', background: colors.warnBg, border: `1px solid ${colors.warnStrong}`, borderRadius: 8, color: colors.warn, fontSize: '0.9rem', fontWeight: 500 }}>
          🎁 NV retenida: {nv.retenida.motivo === 'gift_misma_persona'
            ? 'el cliente canjeó una gift card que parece haber comprado él mismo.'
            : 'el cliente canjeó una gift card heredada de Magento.'}
          {' '}{nv.retenida.aprobacion === 'aprobada'
            ? 'Aprobada por Telegram — sigue su circuito normal.'
            : nv.retenida.aprobacion === 'rechazada'
              ? 'Rechazada por Telegram.'
              : 'Esperando tu aprobación por Telegram (pendiente).'}
          {nv.retenida.at && <span style={{ color: colors.textSec, fontWeight: 400 }}> · retenida el {fmtFechaHora(nv.retenida.at)}</span>}
        </div>
      )}
      {/* Fila del circuito de recupero (Martin, 8 sep: "dejalos como estaban
          y agregá los nuevos abajo") — los botones de siempre quedan arriba
          intactos; el circuito tiene su propia fila. */}
      {/* La fila se dibuja en TODAS las NV porque lleva el 📋 Copiar, que
          sirve en cualquier estado (Martin, 22 sep 2026: "todas las nv deben
          tener el botón de copiar"). Rótulo y botones del circuito, solo
          cuando la NV está en persecución o archivada. */}
      {(() => {
        const enCircuito = rolOperativo && (nv.en_persecucion || nv.archivado)
        // Fraude / bloquear IP: en todo pedido web que todavía no se facturó,
        // pagado o no (Martin, 19 oct 2026, LGR-20261019-V8USFR). Si ya entró
        // plata, el cobro queda marcado "A devolver".
        const esPedidoWeb = !!nv.pedido_web_id || ['storefront', 'magento'].includes(nv.origen)
        const puedeFraude = rolOperativo && !nv.archivado && nv.estado === 'borrador' && (nv.en_persecucion || esPedidoWeb)
        const cobrosOnline = (nv.pagos || []).filter(p => p.online && ['pendiente', 'confirmado', 'acreditado'].includes(p.estado))
        const avisoPlata = cobrosOnline.length
          ? `\n\nATENCIÓN: este pedido ya tiene ${cobrosOnline.length === 1 ? 'un cobro online' : `${cobrosOnline.length} cobros online`} por $ ${fmtMonto(cobrosOnline.reduce((a, p) => a + Number(p.monto_imputado || p.importe || 0), 0), 0)}. Queda marcado "A devolver": hay que devolver la plata en la pasarela (Mercado Pago / Mobbex).`
          : ''
        return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 1rem', ...(enCircuito ? { padding: '8px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 } : {}) }}>
          {enCircuito && (
          <span style={{ fontSize: '0.8rem', color: '#1d4ed8', marginRight: 4 }}>
            {nv.archivado
              ? '🗄 Pedido archivado'
              : `🤖 Pedido en persecución automática${nv.dejada ? ' · dejado al sistema' : ''}`}
          </span>
          )}
          {enCircuito && !nv.archivado && (<>
          <Boton
            variante="secondary" sm
            title={nv.dejada
              ? 'La dejaste al sistema: está fuera de tu listado y el recupero sigue mandando mails. Click para volver a trabajarla.'
              : 'Sale de tu listado y la sigue trabajando el sistema (los mails continúan; si paga vuelve sola, a las 72 h se archiva sola)'}
            onClick={async () => {
              try {
                if (nv.dejada) await notasVentaApi.retomar([Number(id)])
                else await notasVentaApi.dejar([Number(id)])
                await cargar({ silencioso: true })
                setMensaje(nv.dejada ? 'De vuelta en tu listado (los mails nunca pararon).' : 'Dejada al sistema — sale de tu listado, los mails siguen.')
              } catch (err) {
                setError(err.response?.data?.message || 'No se pudo cambiar.')
              }
            }}
          >
            {nv.dejada ? '↩ Retomar' : '🤖 Dejar al sistema'}
          </Boton>
          {nv.en_persecucion && (
          <Boton
            variante="secondary" sm
            title="Corta los mails de recupero (y el WhatsApp automático) SIN archivar la NV: el pedido sigue en tu listado"
            onClick={async () => {
              if (!window.confirm('¿Frenar los mails de recupero? Al cliente no se le manda nada más. La NV sigue activa.')) return
              try {
                await notasVentaApi.frenarMails(Number(id))
                await cargar({ silencioso: true }); setMensaje('Mails frenados — al cliente no le llega nada más. La NV sigue activa.')
              } catch (err) { setError(err.response?.data?.message || 'No se pudieron frenar los mails.') }
            }}
          >✉️ Frenar mails</Boton>
          )}
          <Boton
            variante="secondary" sm
            title="Descartar como DUPLICADO: corta los mails de recupero y el pedido sale de circulación"
            onClick={async () => {
              try {
                await notasVentaApi.archivar([Number(id)], 'duplicado')
                await cargar({ silencioso: true }); setMensaje('Descartada como duplicado — la persecución se cortó.')
              } catch (err) { setError(err.response?.data?.message || 'No se pudo descartar.') }
            }}
          >📄 Duplicado</Boton>
          </>)}
          <Boton
            variante="secondary" sm
            title="Copia NV, cliente, productos e importe para pegar en el chat de Ventas"
            onClick={async () => {
              const ok = await copiarTexto(textoParaWhatsApp())
              setCopiado(ok ? 'listo' : 'error')
              setTimeout(() => setCopiado(null), 2200)
            }}
          >{copiado === 'listo' ? '✓ Copiado' : copiado === 'error' ? 'No se pudo copiar' : '📋 Copiar'}</Boton>
          {puedeFraude && (
          <Boton
            variante="destructive" sm
            title="Descartar como FRAUDE: corta los mails y manda al cliente a la LISTA NEGRA (nada vuelve a entrar a su nombre)"
            onClick={async () => {
              if (!window.confirm(`¿Confirmás FRAUDE? ${nv.cliente?.nombre || 'El cliente'} va a la LISTA NEGRA y no va a poder operar más.${avisoPlata}`)) return
              try {
                await notasVentaApi.archivar([Number(id)], 'fraude')
                await cargar({ silencioso: true }); setMensaje('Descartada como FRAUDE — cliente en lista negra.')
              } catch (err) { setError(err.response?.data?.message || 'No se pudo descartar.') }
            }}
          >🚫 Fraude</Boton>
          )}
          {/* Compra a nombre de otra persona (Martin, 11 oct 2026, LW-000751):
              se corta la IP de donde salió y el cliente queda limpio. */}
          {puedeFraude && nv.origen === 'storefront' && (
          <Boton
            variante="secondary" sm
            title="Descartar como FRAUDE hecho con el nombre de otra persona: corta los mails y BLOQUEA LA IP de la compra. El cliente NO va a lista negra (puede seguir comprando)"
            onClick={async () => {
              if (!window.confirm(`¿Bloquear la IP de esta compra? Se descarta el pedido y nadie vuelve a entrar a la tienda desde esa IP. ${nv.cliente?.nombre || 'El cliente'} NO va a lista negra.${avisoPlata}`)) return
              try {
                await notasVentaApi.archivar([Number(id)], 'fraude_ip')
                await cargar({ silencioso: true }); setMensaje('Descartada como fraude — IP bloqueada, el cliente sigue habilitado.')
              } catch (err) { setError(err.response?.data?.errors?.motivo?.[0] || err.response?.data?.message || 'No se pudo bloquear la IP.') }
            }}
          >🌐 Bloquear IP</Boton>
          )}
          {/* Cobro por WhatsApp desde la LÍNEA DEL SISTEMA (Martin, 26 sep 2026):
              los mismos tres botones que en Carritos abandonados, solo si la NV
              tiene pedido web. En principio lo administra solo el super admin. */}
          {nv.whatsapp_puede_enviar && nv.whatsapp && nv.pedido_web_id && (
            <BotonesCobroWA
              pedidoId={nv.pedido_web_id}
              whatsapp={nv.whatsapp}
              onActualizado={(w) => { setNv(n => ({ ...n, whatsapp: w })); setMensaje('WhatsApp de cobro enviado desde la línea del sistema.') }}
              onError={setError}
            />
          )}
        </div>
      ) })()}

      {mensaje && <div style={styles.success}>{mensaje}</div>}
      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Nota del checkout: instrucción del COMPRADOR — va arriba de todo,
          a lo ancho, para que el vendedor la lea antes de tocar el pedido. */}
      <NotaClienteBanner nota={nv.nota_cliente} />

      {/* El documento, el email o el teléfono de este cliente están en otra
          ficha de la base (sugerencia #123). Arriba, a lo ancho: es lo primero
          que el operador tiene que ver al entrar. Se sugiere, no se aplica. */}
      {nv.clientes_coincidentes?.length > 0 && nv.es_editable && (
        <CoincidenciasClienteBanner nv={nv} onCambiado={() => cargar()} />
      )}

      <div style={styles.grid}>
        {/* Columna izquierda */}
        <div style={{ minWidth: 0 }}>
        {/* Info principal */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Datos del comprobante</h3>
          <div style={styles.infoGrid}>
            <span style={styles.infoLabel}>Cliente</span>
            <span>
              <strong>{nv.cliente?.nombre || '—'}</strong>{nv.cliente?.cuit ? ` — ${nv.cliente.cuit}` : ''}
              {nv.cliente?.id && (
                // La ficha se corrige desde la NV (cambió el teléfono, la
                // dirección o el email por Magento o por teléfono) sin pasar
                // por Clientes. Es LA FICHA: vale para todas sus operaciones.
                <button
                  type="button"
                  onClick={() => setEditarCliente(true)}
                  title="Editar la ficha del cliente sin salir de la NV"
                  style={{ border: 'none', background: 'none', padding: 0, marginLeft: 10, cursor: 'pointer', color: '#2563eb', fontSize: '0.78rem', textDecoration: 'underline' }}
                >✏️ editar ficha</button>
              )}
            </span>
            <span style={styles.infoLabel}>Nº Cliente</span>
            <span>
              {nv.cliente?.codigo_presea
                ? <code style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.85rem' }}>{nv.cliente.codigo_presea}</code>
                : <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600 }}>Pendiente</span>}
              {nv.cliente?.codigo_magento ? <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: 8 }}>· Magento: {nv.cliente.codigo_magento}</span> : null}
              {nv.cliente?.id && nv.cliente?.codigo_presea && (
                // Historia del cliente (cta cte de Presea) con el último año ya filtrado.
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date(); d.setFullYear(d.getFullYear() - 1)
                    const ddmmaaaa = (x) => `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`
                    navigate('/clientes/historia', { state: { cliente: nv.cliente, desde: ddmmaaaa(d), hasta: ddmmaaaa(new Date()) } })
                  }}
                  title="Ver la historia del cliente del último año"
                  style={{ border: 'none', background: 'none', padding: 0, marginLeft: 10, cursor: 'pointer', color: '#2563eb', fontSize: '0.78rem', textDecoration: 'underline' }}
                >📜 historia (último año)</button>
              )}
              {nv.cliente?.id && (
                // Historial CRM: ventas, entregas, opiniones, mensajes… del último año.
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date(); d.setFullYear(d.getFullYear() - 1)
                    const ddmmaaaa = (x) => `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`
                    navigate('/clientes/historia', { state: { cliente: nv.cliente, vista: 'crm', desde: ddmmaaaa(d), hasta: ddmmaaaa(new Date()) } })
                  }}
                  title="Ver el historial CRM del cliente (último año)"
                  style={{ border: 'none', background: 'none', padding: 0, marginLeft: 10, cursor: 'pointer', color: '#2563eb', fontSize: '0.78rem', textDecoration: 'underline' }}
                >📋 historial CRM</button>
              )}
            </span>
            {/* El mail del cliente: es por donde salen los comprobantes y el
                seguimiento, así que hay que poder verlo (y escribirle) sin ir
                a la ficha. */}
            <span style={styles.infoLabel}>Email</span>
            <span>
              {nv.email_contacto
                ? <span>
                    <a href={`mailto:${nv.email_contacto}`} style={styles.mailLink}>{nv.email_contacto}</a>
                    {nv.email_contacto_propio
                      ? <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: 8 }}>· contacto elegido para este pedido{nv.cliente?.email && nv.cliente.email.toLowerCase() !== nv.email_contacto.toLowerCase() ? ` (ficha: ${nv.cliente.email})` : ''}</span>
                      : !nv.cliente?.email
                        ? <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: 8 }}>· del comprador (la ficha facturada no tiene email)</span>
                        : null}
                  </span>
                : <span style={{ color: '#92400e' }}>Sin email — no va a recibir los comprobantes</span>}
            </span>
            {/* El teléfono va acá y no en el bloque de envío: es como se
                coordina la entrega y como se llama al cliente si algo falla, y
                en una venta sin entrega (una cena, una gift card) ese bloque no
                se dibuja — el teléfono quedaba invisible. */}
            <span style={styles.infoLabel}>Teléfono</span>
            <span>
              {(nv.cliente?.telefono_wa || nv.cliente?.telefono)
                ? <a
                    href={waHref(nv.cliente.telefono_wa || nv.cliente.telefono)}
                    target="_blank" rel="noreferrer"
                    style={styles.mailLink}
                  >📱 {nv.cliente.telefono_wa || nv.cliente.telefono}</a>
                : <span style={{ color: '#92400e' }}>Sin teléfono — no se puede coordinar la entrega</span>}
            </span>
            <span style={styles.infoLabel}>Fecha</span>
            <span>
              {fmtFecha(nv.fecha)}
              {/* Hora del pedido (created_at en hora ART) — mismo criterio que
                  el listado /notas-venta: la fecha de la NV puede estar
                  retro-datada, la hora es la de la CARGA. */}
              <span style={{ marginLeft: 6, color: colors.textPlh }}>{fmtHoraDe(nv.created_at)}</span>
            </span>
            <span style={styles.infoLabel}>Tipo factura</span>
            <span>
              {(() => {
                // Badge de letra A/B/C — mismo patrón que /facturas (regla
                // Martin 19 jun: "el tipo (letra) hacelo igual en NV").
                // Reusa tokens semánticos: A=link (azul), B=ok (verde),
                // C=warn (ámbar). Fallback gris si la letra no está cargada.
                const letra = nv.tipo_factura || ''
                const col = letra === 'A' ? { bg: '#eff6ff', color: '#1d4ed8' }
                          : letra === 'B' ? { bg: '#dcfce7', color: '#166534' }
                          : letra === 'C' ? { bg: '#fef3c7', color: '#92400e' }
                          : { bg: '#f1f5f9', color: '#64748b' }
                return (
                  <span style={{
                    background: col.bg,
                    color: col.color,
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    fontFamily: 'ui-monospace, monospace',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                  }}>{letra || '—'}</span>
                )
              })()}
            </span>
            <span style={styles.infoLabel}>Condición pago</span>
            <span>{nv.condicion_venta || '—'}</span>
            <span style={styles.infoLabel}>Depósito</span>
            <span>{nv.deposito || '—'}</span>
            <span style={styles.infoLabel}>Observaciones</span>
            <span>{nv.observaciones || '—'}</span>
            {nv.indicaciones_packaging && (<>
              <span style={styles.infoLabel}>Packaging</span>
              <span>{nv.indicaciones_packaging}</span>
            </>)}
            <span style={styles.infoLabel}>Vendedor</span>
            <span>
              {/* Pedidos web (store/Magento): el vendedor de la venta es el
                  código 121 (WEB SITE) aunque no haya usuario LGR asignado —
                  mostrarlo, no "—" (pedido Martin 12/08). */}
              {nv.vendedor?.nombre
                || (nv.vendedor_codigo ? <span><code>{nv.vendedor_codigo}</code> — web</span> : '—')}
            </span>
            {/* Quién PROCESÓ el pedido — dato aparte del vendedor (la venta
                web sigue facturando con el 121 WEB SITE). Importa para el
                resto de la cadena: si otro sector tiene una pregunta sobre
                este pedido, acá está a quién hacérsela. */}
            {nv.origen !== 'lgr' && (
              <>
                <span style={styles.infoLabel}>Tomado por</span>
                <span>
                  {nv.tomada_por
                    ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span>{nv.tomada_por}{nv.tomada_at ? <span style={{ color: '#64748b', fontSize: '0.75rem' }}> · {fmtFechaHora(nv.tomada_at)}</span> : null}</span>
                        {/* Lo suelta quien lo tomó; admin/supervisor, el de cualquiera. */}
                        {(user?.id === nv.tomada_por_id || hasAnyRole(['administrador', 'supervisor'])) && (
                          <Boton variante="ghost" sm onClick={soltarPedido} disabled={tomando}>
                            {tomando ? 'Soltando…' : 'Soltar'}
                          </Boton>
                        )}
                      </span>
                    )
                    : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: '#b45309', fontWeight: 600 }}>Sin tomar</span>
                        <Boton variante="primary" sm onClick={tomarPedido} disabled={tomando}>
                          {tomando ? 'Tomando…' : '🙋 Tomar pedido'}
                        </Boton>
                      </span>
                    )}
                </span>
              </>
            )}
            <span style={styles.infoLabel}>Origen</span>
            <span>{nv.origen}{nv.origen_externo_id ? ` · ${nv.origen_externo_id}` : ''}</span>
            {/* Cuenta web logueada al hacer el pedido de la tienda (Martin,
                13 oct 2026). Invitado o pedidos anteriores: sin renglón. */}
            {nv.cuenta_web && (
              <>
                <span style={styles.infoLabel}>Cuenta web</span>
                <span>{nv.cuenta_web.nombre}{nv.cuenta_web.nombre ? ' · ' : ''}<span style={{ color: colors.textMeta }}>{nv.cuenta_web.email}</span></span>
              </>
            )}
            {/* IP del pedido web (sug #64): sirve para rastrear fraude — con
                ella se buscan en /notas-venta todos los pedidos de esa IP.
                Solo la tienen los pedidos del storefront desde el 26/08/2026;
                sin dato, el renglón no aparece. */}
            {nv.ip_pedido && (
              <>
                <span style={styles.infoLabel}>IP del pedido</span>
                <span style={{ fontFamily: 'ui-monospace, monospace' }}>{nv.ip_pedido}</span>
              </>
            )}
          </div>

          {/* "Confirmar mi transferencia" sin comprobante (26 sep 2026): el
              cliente avisó que transfirió; verificar que entró la plata es del
              vendedor. Si además subió el comprobante, abajo está el link. */}
          {nv.transferencia_avisada_at && !nv.comprobante_url && (
            <div style={{ marginTop: '0.9rem', padding: '0.7rem 0.9rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#92400e' }}>
                🏦 El cliente confirmó que hizo la transferencia (sin adjuntar comprobante)
              </div>
              <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: '0.15rem' }}>
                Avisó el {fmtFechaHora(nv.transferencia_avisada_at)} · verificá que entró la plata antes de registrar el cobro
              </div>
            </div>
          )}
          {/* Comprobante de transferencia que subió el cliente desde la web.
              Antes la URL quedaba pegada al final de las observaciones y no
              había forma de encontrarla desde el panel (pasó con LW-000001).
              Acá va como link, para abrirlo antes de acreditar el pago. */}
          {nv.comprobante_url && (
            <div style={{
              marginTop: '0.9rem',
              padding: '0.7rem 0.9rem',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
            }}>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#166534' }}>
                  📎 El cliente adjuntó el comprobante de la transferencia
                </div>
                {nv.comprobante_subido_at && (
                  <div style={{ fontSize: '0.72rem', color: '#15803d', marginTop: '0.15rem' }}>
                    Subido el {fmtFechaHora(nv.comprobante_subido_at)}
                  </div>
                )}
              </div>
              <a
                href={nv.comprobante_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flexShrink: 0,
                  padding: '0.35rem 0.8rem',
                  borderRadius: '5px',
                  border: '1px solid #166534',
                  color: '#166534',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver comprobante ↗
              </a>
            </div>
          )}

          {/* Ficha de despacho: la dirección donde va la mercadería, separada
              de los datos fiscales (que son del cliente). Estructurada por
              campo para que el vendedor la lea sin ambigüedad. */}
          {(() => {
            const d = nv.direccion_envio

            // Sugerencia #47: el backend resuelve la zona de la entrega con el
            // resolver de logística; Interior (más de ~50 km) = va por Andreani.
            // El vendedor que tomó el pedido se entera acá, no en depósito.
            const alertaAndreani = nv.envio_fuera_reparto ? (
              <div style={{ marginTop: '0.6rem', background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: '8px 12px', fontSize: '0.82rem', fontWeight: 500 }}>
                {nv.envio_km_fuera != null
                  ? <>⚠ Entrega a <b>{String(nv.envio_km_fuera).replace('.', ',')} km</b> de Ligier por ruta (más de {nv.envio_km_limite ?? 50}): fuera del reparto propio. El envío se cotiza con los costos de <b>Andreani</b>; elegí cómo despacharlo.</>
                  : <>⚠ Entrega a más de ~50 km (zona Interior): no corresponde transporte propio — el envío va por <b>Andreani</b>.</>}
              </div>
            ) : null

            // Venta sin nada que despachar (cena, evento, gift card): no hay
            // dirección de entrega que mostrar. El criterio lo resuelve el
            // backend para que la pantalla, el remito y Presea coincidan.
            // Envío múltiple (docs/diseno-envio-multiple.md): varios destinos
            // con sus artículos; el remito va como T80 y la planilla de NE se
            // precarga con estos datos.
            if (nv.envio_destinos?.length) {
              return (
                <div style={styles.envioPanel}>
                  <div style={styles.envioHeader}>🚚 Envío múltiple — {nv.envio_destinos.length} destinos</div>
                  {nv.envio_destinos.map((d, i) => (
                    <div key={d.id || i} style={{ padding: '6px 0', borderTop: i ? '1px solid #e2e8f0' : 'none', fontSize: '0.85rem' }}>
                      <div><b>{i + 1}. {d.receptor_nombre || 'Sin receptor'}</b>{d.telefono ? ` · ${d.telefono}` : ''}</div>
                      <div style={{ color: '#475569' }}>{d.direccion_texto || 'Sin dirección'}{d.notas ? ` — ${d.notas}` : ''}</div>
                      <div style={{ color: '#64748b' }}>
                        {(d.items || []).map(it => `${it.cantidad}× ${it.sku}${it.descripcion ? ' ' + it.descripcion : ''}`).join(' · ')}
                        {d.cotizacion && (
                          <> · <b style={{ color: d.cotizacion.pendiente ? '#92400e' : '#334155' }}>
                            {d.cotizacion.pendiente ? 'envío a cotizar aparte' : (d.cotizacion.gratis ? 'envío sin cargo' : `envío $${fmtMonto(d.cotizacion.costo)}`)}
                          </b></>
                        )}
                      </div>
                    </div>
                  ))}
                  <div style={styles.envioNota}>Al enviar el paquete, el remito sale como <b>T80</b> y la planilla de notas de envío se precarga con estos destinos.</div>
                  {alertaAndreani}
                </div>
              )
            }

            if (nv.requiere_entrega === false) {
              return (
                <div style={styles.envioPanel}>
                  <div style={styles.envioHeader}>✨ Sin entrega</div>
                  <div style={styles.envioNota}>
                    Esta venta no despacha mercadería (experiencia o gift card): se factura
                    y se da por entregada sola, sin pasar por logística.
                  </div>
                </div>
              )
            }

            if (d?.retira) {
              return (
                <div style={styles.envioPanel}>
                  <div style={styles.envioHeader}>📦 Envío</div>
                  <div style={styles.envioNota}>Retira en depósito — no se despacha.</div>
                </div>
              )
            }
            const tieneEnvio = d && (d.calle || d.localidad || d.receptor_nombre)
            if (!tieneEnvio) {
              // Se entrega al domicilio fiscal. Lleva el mismo link al mapa que
              // el envío propio: es la dirección a la que va el reparto, así que
              // se tiene que poder verificar igual.
              const dirFiscal = [nv.cliente?.domicilio, nv.cliente?.localidad, nv.cliente?.provincia, nv.cliente?.codigo_postal]
                .filter(Boolean).join(', ')
              return (
                <div style={styles.envioPanel}>
                  <div style={styles.envioHeaderRow}>
                    <span style={styles.envioHeader}>📦 Envío a domicilio del cliente</span>
                    {dirFiscal && (
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dirFiscal)}`}
                        target="_blank" rel="noopener noreferrer" style={styles.envioMapa}>🗺 Ver en mapa</a>
                    )}
                  </div>
                  <div style={styles.envioNota}>
                    Sin dirección propia cargada — se entrega al domicilio fiscal:
                    {' '}<strong>{nv.cliente?.domicilio || '—'}</strong>
                    {nv.cliente?.localidad ? `, ${nv.cliente.localidad}` : ''}
                    {nv.cliente?.provincia ? `, ${nv.cliente.provincia}` : ''}.
                  </div>
                  {alertaAndreani}
                </div>
              )
            }
            const dirLinea = [d.calle, d.localidad, d.provincia, d.codigo_postal].filter(Boolean).join(', ')
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dirLinea)}`
            const campo = (label, value, mono) => (
              <>
                <span style={styles.envioLabel}>{label}</span>
                <span style={mono ? styles.envioMono : styles.envioVal}>{value || '—'}</span>
              </>
            )
            return (
              <div style={styles.envioPanel}>
                <div style={styles.envioHeaderRow}>
                  <span style={styles.envioHeader}>📦 Envío a domicilio</span>
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={styles.envioMapa}>🗺 Ver en mapa</a>
                </div>
                <div style={styles.envioGrid}>
                  {campo('Recibe', d.receptor_nombre)}
                  {campo('Teléfono', d.telefono, true)}
                  {campo('Domicilio', d.calle)}
                  {campo('Localidad', [d.localidad, d.provincia].filter(Boolean).join(', '))}
                  {campo('CP', d.codigo_postal, true)}
                </div>
                {alertaAndreani}
              </div>
            )
          })()}
        </div>



        {/* Comprobantes de la operación — debajo del envío, al ancho de la columna */}
        <CadenaComprobantes nv={nv} onNavigate={navigate} />

        {/* Progreso del envío a Presea (factura → remito → recibo) — mismo ancho */}
        <BannerProgresoEnvio
          polling={polling}
          nv={nv}
          onCerrar={() => setPolling({ estado: 'idle', inicio: 0, error: null })}
        />
        </div>

        {/* Columna derecha: Mensajes del pedido + Historial (pedido Martin 12/08) */}
        <div style={{ minWidth: 0 }}>
        {/* Ediciones de los renglones. Aparte del historial de estados: cambiar
            botellas no es un cambio de estado, pero es lo que explica que el
            importe de la nota no coincida con el del pedido web. */}
        {nv.ediciones?.length > 0 && (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Ediciones de los artículos</h3>
            <div style={styles.historial}>
              {nv.ediciones.map((e, i) => (
                <div key={i} style={styles.historialItem}>
                  <div style={styles.historialMeta}>
                    <span><strong>{e.usuario || 'Sistema'}</strong></span>
                    <span>{fmtFechaHora(e.fecha)}</span>
                  </div>
                  <div style={styles.historialMotivo}>{e.detalle}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mensajes libres del pedido ("está esperando un vino del proveedor").
            Abierto a cualquier usuario, en cualquier estado de la NV. Distinto
            de los Avisos (dirigidos a una persona con acuse): esto es la
            cartelera del pedido. */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Mensajes del pedido</h3>
          {(nv.mensajes || []).length === 0 ? (
            <p style={styles.emptyText}>Sin mensajes. Dejá acá cualquier aclaración sobre este pedido (ej: "esperando un vino del proveedor").</p>
          ) : (
            <div style={styles.historial}>
              {nv.mensajes.map(m => (
                <div key={m.id} style={styles.historialItem}>
                  <div style={styles.historialMeta}>
                    <span><strong>{m.usuario || 'Sistema'}</strong></span>
                    <span>{fmtFechaHora(m.fecha)}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: '0.8125rem', color: '#1e293b', whiteSpace: 'pre-wrap' }}>{m.texto}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: '0.75rem', alignItems: 'flex-start' }}>
            <textarea
              value={msgTexto}
              onChange={e => setMsgTexto(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMensaje() } }}
              placeholder="Escribí un mensaje… (Enter envía, Shift+Enter salto de línea)"
              rows={2}
              style={{ flex: 1, padding: '0.5rem 0.75rem', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '0.8125rem', fontFamily: 'inherit', resize: 'vertical' }}
            />
            <Boton variante="secondary" sm onClick={enviarMensaje} disabled={msgEnviando || !msgTexto.trim()}>
              {msgEnviando ? 'Enviando…' : 'Agregar'}
            </Boton>
          </div>
        </div>


        {/* Avisos internos sobre este pedido ("seguí el tema con el cliente"). */}
        <AvisosCard tipo="nv" id={nv.id} />

        {/* Historial */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Historial de estados</h3>
          {nv.historial?.length === 0 ? (
            <p style={styles.emptyText}>Sin historial.</p>
          ) : (
            <div style={styles.historial}>
              {nv.historial?.map((h, i) => (
                <div key={i} style={styles.historialItem}>
                  <div style={styles.historialEstados}>
                    {h.estado_anterior && <span style={styles.estadoViejo}>{h.estado_anterior}</span>}
                    {h.estado_anterior && <span style={styles.flecha}>→</span>}
                    <EstadoBadge estado={h.estado_nuevo} />
                  </div>
                  <div style={styles.historialMeta}>
                    <span>{h.usuario || 'Sistema'}</span>
                    <span>{fmtFechaHora(h.fecha)}</span>
                  </div>
                  {h.motivo && <div style={styles.historialMotivo}>{h.motivo}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Ítems */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md }}>
          <h3 style={styles.cardTitle}>Artículos</h3>
          {nv.estado === 'borrador' && nv.items?.length > 0 && (
            <Boton variante="secondary" sm onClick={() => setShowPedirDeposito(true)}
              title="Le pregunta a depósito si hay los productos que elijas; la respuesta vuelve a esta NV">
              🏬 Pedir stock a depósito
            </Boton>
          )}
        </div>
        <table style={styles.itemTable}>
          <thead>
            <tr>
              {['Código', 'Descripción', 'Cant.', 'Precio lista', 'Desc. %', 'Precio final', 'Subtotal', 'Modificado'].map(h => (
                <th key={h} style={styles.itemTh}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nv.items?.map((it, i) => (
              <tr key={i} style={it.precio_modificado ? { background: '#fefce8' } : {}}>
                {/* Alias hermanado (SKU web/Magento → código de Presea): se
                    muestra el FACTURABLE, que es lo que sale en la factura; el
                    alias queda como nota chica. */}
                <td style={styles.itemTd}>
                  <code>{it.articulo?.factura_como?.codigo_alfa || it.articulo?.codigo_alfa}</code>
                  {it.articulo?.factura_como && (
                    <div style={{ fontSize: '0.68rem', color: '#64748b' }}>alias {it.articulo.codigo_alfa}</div>
                  )}
                </td>
                <td style={styles.itemTd}>
                  {it.descripcion?.trim() ? (
                    <span title={`Detalle propio — el artículo real es: ${it.articulo?.factura_como?.descripcion || it.articulo?.descripcion}`}>
                      {it.descripcion}
                      <span style={{ marginLeft: 6, fontSize: '0.68rem', color: '#7c3aed', background: '#f5f3ff', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>detalle propio</span>
                    </span>
                  ) : (it.articulo?.factura_como?.descripcion || it.articulo?.descripcion)}
                
                  {it.turno && (
                    // Turno elegido por el comprador (experiencias): con esto
                    // Ventas arma las listas de cada turno.
                    <div style={{ marginTop: 3 }}>
                      <span style={{ fontSize: '0.72rem', color: '#0f766e', background: '#f0fdfa', border: '1px solid #99f6e4', padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>🕘 Turno {it.turno}</span>
                    </div>
                  )}
                </td>
                <td style={styles.itemTd}>{it.cantidad}</td>
                <td style={styles.itemTd}>${fmtMonto(it.precio_lista)}</td>
                <td style={styles.itemTd}>{parseFloat(it.descuento) || 0}%</td>
                <td style={{ ...styles.itemTd, fontWeight: 600, color: it.precio_modificado ? '#b45309' : '#166534' }}>
                  ${fmtMonto(it.precio_final)}
                </td>
                <td style={styles.itemTd}>${fmtMonto(it.subtotal)}</td>
                <td style={styles.itemTd}>
                  {it.precio_modificado ? <span style={styles.pinBadge}>Con PIN</span> : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={styles.totalesRow}>
          <span>Subtotal: <strong>${fmtMonto(nv.total_sin_descuento || 0)}</strong></span>
          {parseFloat(nv.descuento_gral) > 0 && (
            <span>Desc. gral: <strong>{parseFloat(nv.descuento_gral) || 0}%</strong></span>
          )}
          <span style={styles.totalFinal}>
            TOTAL: <strong>${fmtMonto(nv.total || 0)}</strong>
          </span>
          {/* Facturada, el importe REAL de la factura (con percepciones) lo
              trae DETALLE_COMPRO (monto_a_cobrar). Mostrar solo el total
              interno hacía decir que la factura era de menos plata de la que
              es (caso 5300035435: $3.327.500 vs $3.465.000 — Martin, 7 sep 2026). */}
          {nv.numero_presea && nv.monto_a_cobrar != null && Math.abs(Number(nv.monto_a_cobrar) - Number(nv.total || 0)) > 0.005 && (
            <span style={{ ...styles.totalFinal, color: '#1e40af' }}>
              TOTAL FACTURA (c/ percepciones): <strong>${fmtMonto(nv.monto_a_cobrar)}</strong>
            </span>
          )}
        </div>
        {/* Percepciones estimadas (cliente RI, NV aún sin facturar): el número
            que el vendedor cobra con el link de pago o la transferencia. */}
        {nv.percepciones_estimadas?.aplica && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: '1.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
            {nv.percepciones_estimadas.percep_iva > 0 && (
              <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                Percep. IVA: <strong>${fmtMonto(nv.percepciones_estimadas.percep_iva)}</strong>
              </span>
            )}
            {nv.percepciones_estimadas.percep_iibb > 0 && (
              <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                Percep. IIBB CABA{nv.percepciones_estimadas.alicuota_iibb ? ` (${nv.percepciones_estimadas.alicuota_iibb}%)` : ''}: <strong>${fmtMonto(nv.percepciones_estimadas.percep_iibb)}</strong>
              </span>
            )}
            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1e40af' }}>
              TOTAL A COBRAR: ${fmtMonto((parseFloat(nv.total) || 0) + nv.percepciones_estimadas.total)}
            </span>
          </div>
        )}
      </div>

      <ConsultasDepositoCard
        consultas={deposito.consultas}
        onCancelar={async (c) => { await consultasStockApi.cancelar(c.id); deposito.recargar() }}
      />
      {showPedirDeposito && (
        <PedirStockDepositoModal
          nvId={nv.id}
          renglones={(nv.items || []).map(it => ({ articulo_id: it.articulo_id, codigo_alfa: it.articulo?.codigo_alfa, descripcion: it.articulo?.descripcion || it.descripcion, cantidad: it.cantidad }))}
          onClose={() => setShowPedirDeposito(false)}
          onCreada={() => deposito.recargar()}
        />
      )}
      <LinksPagoNVCard nv={nv} onChange={() => cargar()} />
      {/* Gift cards: las que esta venta emite al facturar (con los datos del
          regalo) y los canjes reservados sobre ella. La gift card es AL
          PORTADOR — se canjea con el código, sin importar de qué cliente sea.
          Ojo: esta card se sacó del detalle el 02/07/2026 (b0c7846a) y quedó
          huérfana hasta el 21/08; sin ella el vendedor no tiene NINGUNA forma
          de cargar una gift card en la venta. */}
      <GiftCardsNVCard nv={nv} onChange={() => cargar()} />

      {/* Cliente ambiguo (import Magento con email en varias fichas): el envío
          a Presea está frenado hasta confirmar o cambiar el cliente. */}
      {nv.cliente_ambiguo && nv.es_editable && (
        <ClienteAmbiguoBanner nv={nv} onResuelto={() => cargar()} />
      )}

      {/* Lo que le falta a este pedido para cerrarse. Las instrucciones del
          motor terminan en "tocá ✓ Verificar", y ese botón vivía sólo en
          SISTEMA → ERP Presea, que es admin: el 04/08/2026 un vendedor emitió
          la nota de crédito en Presea y se quedó sin dónde completarla. La
          tarea va donde está el trabajo. */}
      <PendienteEnPreseaCard nvId={nv.id} onResuelto={() => cargar()} />

      {/* Este comprador ya facturó antes con otra razón social. Se sugiere,
          no se aplica solo: el que decide es quien está mirando la factura. */}
      {nv.sugerencia_facturacion && (
        <SugerenciaFacturacionBanner nv={nv} onCambiado={() => cargar()} />
      )}




      {/* Marca "sin recibo": ventas que NUNCA van a tener cobro (factura por
          canje de marketing al vendedor interno, prueba). El ciclo F/P/R del
          listado las da por COMPLETAS con factura + remito. */}
      {puedeGestionarComprobantes && (nv.sin_recibo ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '-0.5rem 0 1rem', fontSize: '0.8125rem', color: '#475569' }}>
          <span style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '4px 10px' }}>
            🚫💰 <strong>No lleva recibo</strong> — {nv.sin_recibo_motivo || 'sin cobro'}
          </span>
          <Boton variante="ghost" sm onClick={async () => {
            await notasVentaApi.quitarSinRecibo(id); cargar()
          }}>↩ Quitar marca</Boton>
        </div>
      ) : (
        <div style={{ margin: '-0.5rem 0 1rem' }}>
          {sinCobrosVigentes && (
            <Boton variante="ghost" sm title="Para ventas que no llevan cobro (canje MKT, factura al vendedor interno): la operación queda COMPLETA con factura + remito"
              onClick={async () => {
                const motivo = window.prompt('¿Por qué esta venta no lleva recibo? (ej: "Canje MKT — vendedor 1")')
                if (!motivo?.trim()) return
                try {
                  await notasVentaApi.sinRecibo(id, motivo.trim()); cargar()
                } catch (err) {
                  setError(err.response?.data?.message || 'No se pudo marcar.')
                }
              }}>
              🚫💰 Marcar "sin recibo"…
            </Boton>
          )}
          {/* Recibo hecho EN PRESEA (retenciones): se importa por número.
              Disponible SIEMPRE — también con la NV procesada o con cobros
              parciales: el recibo del saldo pudo hacerse allá (Martin, 7 sep 2026). */}
          <Boton variante="ghost" sm style={{ marginLeft: sinCobrosVigentes ? 6 : 0 }}
            title="El recibo se hizo en Presea (p. ej. con retenciones): cargá su número y LGR lo trae a esta NV como cobro acreditado"
            onClick={() => setReciboPresea(true)}>
            🧾 Recibo hecho en Presea…
          </Boton>
        </div>
      ))}

      {/* Modal Enviar a Presea */}
      {showEnviarModal && (
        <EnviarPreseaModal
          nv={nv}
          onClose={() => setShowEnviarModal(false)}
          onConfirm={handleEnvioCompleto}
          enviando={enviando}
        />
      )}

      {/* Modal NC / Devolución (anulación con gate de Presea) */}
      {showNcModal && (
        <NcDevolucionModal
          nvId={id}
          esAdmin={hasAnyRole(['administrador'])}
          onCerrar={() => setShowNcModal(false)}
          onHecho={() => cargar({ silencioso: true })}
        />
      )}

      {showRefacturar && (
        <RefacturacionModal
          nvId={id}
          onCerrar={() => setShowRefacturar(false)}
          onHecho={() => cargar({ silencioso: true })}
        />
      )}

      {/* Recibo hecho en Presea → importado a la NV */}
      {reciboPresea && (
        <ModalReciboPresea
          nv={nv}
          onClose={() => setReciboPresea(false)}
          onImportado={(pago) => {
            setReciboPresea(false)
            setMensaje(`Recibo ${pago.formulario_presea || ''} ${pago.numero_presea} importado desde Presea (${fmtMonto(pago.importe)}).`)
            cargar({ silencioso: true })
          }}
        />
      )}

      {comprobantesPresea && (
        <ModalComprobantesPresea
          nv={nv}
          onClose={() => setComprobantesPresea(false)}
          onVinculado={(r) => {
            setComprobantesPresea(false)
            setMensaje(`Vinculada a la factura ${r.factura} y al remito ${r.remito} de Presea. El remito ya está en logística.`)
            cargar({ silencioso: true })
          }}
        />
      )}

      {/* Ficha del cliente editable desde la NV (guarda en Clientes) */}
      {editarCliente && nv.cliente?.id && (
        <ModalAltaCliente
          cliente={nv.cliente}
          onClose={() => setEditarCliente(false)}
          onGuardado={() => { setEditarCliente(false); cargar({ silencioso: true }) }}
        />
      )}
    </div>
  )
}

const REMITO_DIR_VACIO = { receptor_nombre: '', telefono: '', calle: '', piso: '', localidad: '', provincia: '', codigo_postal: '' }
const PAGO_VACIO = () => ({ _key: Math.random(), fecha: hoyISO(), medio_pago_id: '', importe: '', nro_cupon_cheque: '', cod_autorizacion: '', id_transaccion: '', cuotas: 1, lote: '', fecha_pago: '', n_bco: '', n_bco_nombre: '' })

function EnviarPreseaModal({ nv, onClose, onConfirm, enviando }) {
  const nvYaProcesada = !nv.es_editable
  // Canje MKT: el remito virtual se pre-tilda (la NV no lleva recibo — el
  // check de recibo queda apagado y la marca sin_recibo ya viene de la NV).
  const [checks, setChecks] = useState({ nv: !nvYaProcesada, factura: false, remito: !!nv.es_mkt, recibo: false })
  // SOLO REMITO (Martin, 8 ago): mercadería que viaja sin factura ni recibo
  // (traslado, reposición, muestra). La NV va a Presea solo como vehículo y el
  // despacho usa FORMULARIO='REMITO'. Reemplaza al viejo "remito libre".
  const [soloRemito, setSoloRemito] = useState(false)
  const toggleSoloRemito = () => setSoloRemito(v => {
    const on = !v
    if (on) setChecks(c => ({ ...c, nv: true, remito: true, recibo: false }))
    return on
  })
  // Si la NV viene de Magento con shipping_address null, MagentoService persiste
  // {retira:true} en direccion_envio para que el modal preseleccione esta opción.
  const vinoComoRetira = nv.direccion_envio?.retira === true
  // Canje MKT: la entrega es virtual (sin logística) — se preselecciona.
  // Envío múltiple (varios destinos cargados en la NV): el remito es T80 y la planilla nace precargada.
  const [tipoRemito, setTipoRemito] = useState(nv.es_mkt ? '0' : (vinoComoRetira ? 'retira' : (nv.envio_destinos?.length ? '80' : '90')))
  const [usarDirFact, setUsarDirFact] = useState(false)
  const [remito90, setRemito90] = useState(REMITO_DIR_VACIO)
  const [pagos, setPagos] = useState([PAGO_VACIO()])
  const [medios, setMedios] = useState([])
  // Puntos de retiro: se imprimen en el remito para que el depósito sepa en
  // qué sucursal separar la mercadería.
  const [sucursales, setSucursales] = useState([])
  const [sucursalRetiro, setSucursalRetiro] = useState(nv.direccion_envio?.sucursal || '')
  // Valor declarado OPCIONAL del remito a domicilio (sug #38): vacío por
  // defecto, estado propio para que el toggle de dirección no lo pise.
  const [valorDeclarado, setValorDeclarado] = useState('')

  useEffect(() => {
    mediosPagoApi.listar(true).then(({ data }) => setMedios(data)).catch(() => {})
    api.get('/sucursales-retiro')
      .then(({ data }) => setSucursales(Array.isArray(data) ? data : []))
      .catch(() => setSucursales([]))
  }, [])

  // Autocompletar el remito T90 con la dirección de ENVÍO del pedido (Magento),
  // así no hay que re-tipearla. Si el pedido no trae envío separado, queda vacío
  // y el operador puede usar la dirección de facturación (checkbox de abajo).
  const dirEnvio = nv.direccion_envio
  const tieneDirEnvio = Boolean(dirEnvio && (dirEnvio.calle || dirEnvio.localidad || dirEnvio.receptor_nombre))
  useEffect(() => {
    if (tieneDirEnvio) {
      const prov = matchProvincia(dirEnvio.provincia || '')
      setRemito90({
        receptor_nombre: dirEnvio.receptor_nombre || '',
        telefono:        dirEnvio.telefono || '',
        calle:           dirEnvio.calle || '',
        piso:            dirEnvio.piso || '',
        localidad:       dirEnvio.localidad || '', // tal cual vino: la dirección no se recorta ni se canoniza a la fuerza
        provincia:       prov,
        codigo_postal:   dirEnvio.codigo_postal || '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const esCabaR = esCapitalFederal(remito90.provincia)

  const toggle = k => setChecks(c => ({ ...c, [k]: !c[k] }))
  const setR90 = k => e => setRemito90(f => ({ ...f, [k]: e.target.value }))

  // Al tipear el CP (4 dígitos) sugiere la localidad según los datos reales:
  // la más frecuente entre los clientes con ese CP (GET /localidades/por-cp).
  // No pisa una localidad ya elegida a mano; completa la provincia si falta.
  const onCpR90 = (e) => {
    const v = e.target.value
    setRemito90(f => ({ ...f, codigo_postal: v }))
    const cp = v.replace(/\D/g, '').slice(0, 4)
    if (cp.length < 4) return
    api.get('/localidades/por-cp', { params: { cp } })
      .then(({ data }) => {
        if (!data?.localidad) return
        setRemito90(f => {
          if (f.localidad && f.localidad.trim()) return f // respeta lo elegido a mano
          const prov = f.provincia || (data.provincia ? matchProvincia(data.provincia) : '')
          const loc = prov === 'Buenos Aires' ? matchLocalidad(data.localidad) : data.localidad
          return { ...f, localidad: loc, provincia: prov || f.provincia }
        })
      })
      .catch(() => { /* sin datos para ese CP: no pasa nada */ })
  }

  const handleUsarDirFact = (checked) => {
    setUsarDirFact(checked)
    if (checked) {
      const prov = matchProvincia(nv.cliente?.provincia || '')
      setRemito90({
        ...REMITO_DIR_VACIO,
        receptor_nombre: nv.cliente?.nombre || '',
        telefono: nv.cliente?.telefono || '',
        calle: nv.cliente?.domicilio || '',
        localidad: prov === 'Buenos Aires' ? matchLocalidad(nv.cliente?.localidad || '') : (nv.cliente?.localidad || ''),
        provincia: prov,
        codigo_postal: nv.cliente?.codigo_postal || '',
      })
    } else {
      setRemito90(REMITO_DIR_VACIO)
    }
  }

  const setPago = (idx, key, val) => setPagos(prev => prev.map((p, i) => {
    if (i !== idx) return p
    // Cambio de medio limpia el N_BCO (era de otro tipovalor).
    if (key === 'medio_pago_id') return { ...p, medio_pago_id: val, n_bco: '', n_bco_nombre: '' }
    return { ...p, [key]: val }
  }))
  const setBancoPago = (idx, sel) => setPagos(prev => prev.map((p, i) =>
    i === idx ? { ...p, n_bco: sel?.n_bco ?? '', n_bco_nombre: sel?.nombre ?? '' } : p
  ))
  const agregarPago = () => setPagos(prev => [...prev, PAGO_VACIO()])
  const quitarPago = (idx) => setPagos(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx))

  const totalCobrado = pagos.reduce((acc, p) => acc + (parseMonto(p.importe) || 0), 0)

  // UNA sola magnitud "a cobrar" (sug #53): facturada → el importe fiscal real
  // de DETALLE_COMPRO (monto_a_cobrar, percepciones incluidas); sin facturar →
  // renglones + percepciones estimadas — el MISMO "TOTAL A COBRAR" que la
  // pantalla le mostró al vendedor. Comparar contra nv.total pelado marcaba
  // una "diferencia" falsa en toda NV con percepciones.
  const percepEstimadas = nv.percepciones_estimadas?.aplica
    ? (parseFloat(nv.percepciones_estimadas.total) || 0)
    : 0
  const totalACobrar = nv.numero_presea
    ? (parseFloat(nv.monto_a_cobrar ?? nv.total) || 0)
    : (parseFloat(nv.total) || 0) + percepEstimadas

  // Un remito ANULADO no cuenta: cuando el despacho en Presea falla, el
  // remito que nació con el envío se anula solo, y el modal tiene que dejar
  // elegir el remito de nuevo (antes decía "Ya creado" con el anulado).
  const remitosVigentes = (nv.remitos || []).filter(r => r.estado !== 'anulado')
  const yaTieneRemito = remitosVigentes.length > 0
  // Pagos ya vinculados a la NV (típico de pedidos Magento). Si los hay,
  // el modal NO muestra el form manual de recibo: los confirmados se
  // auto-incluyen en el paquete (el chain del backend los manda solos).
  const pagosVinculados = nv.pagos || []
  // Un pago `acreditado` ya tiene su recibo emitido en Presea: cuenta como
  // cubierto, no como "sin confirmar" (reabrir el modal después del chain
  // mostraba un aviso amarillo sobre cobros ya emitidos).
  const pagosConfirmados = pagosVinculados.filter(p => ['confirmado', 'acreditado'].includes(p.estado))
  const pagosPendientes  = pagosVinculados.filter(p => p.estado === 'pendiente')
  const hayPagosVinculados = pagosVinculados.length > 0

  // Saldo: lo que la NV factura menos lo ya cobrado. Un pedido de la tienda
  // puede quedar debiendo cuando después se le agrega un artículo que no estaba
  // en el pedido original — ahí hace falta sumar un recibo COMPLEMENTARIO por la
  // diferencia antes de mandar el paquete, o la factura sale con saldo abierto.
  const cobradoVigente = pagosVinculados
    .filter(p => !['anulado', 'rechazado', 'devuelto'].includes(p.estado))
    .reduce((acc, p) => acc + (parseFloat(p.monto_imputado ?? p.importe) || 0), 0)
  const saldoNv = Math.round((totalACobrar - cobradoVigente) * 100) / 100
  const faltaCobrar = saldoNv > 0.01
  // El form manual aparece si no hay cobros vinculados (caso de siempre) o si
  // los hay pero quedó saldo (el complementario).
  const mostrarFormRecibo = !hayPagosVinculados || faltaCobrar
  const ningunoSeleccionado = (nvYaProcesada ? true : !checks.nv) && (yaTieneRemito ? true : !checks.remito) && !checks.recibo && pagosConfirmados.length === 0

  // Regla (Martin, 7 jun): si la NV viaja a Presea, TODOS los artículos deben
  // tener código Presea — el envío se bloquea hasta completarlos (inline en el banner).
  const [codigosPendientes, setCodigosPendientes] = useState(() => {
    const ids = new Set()
    for (const it of (nv.items || [])) if (it.articulo && !it.articulo.codigo_presea && !it.articulo.factura_como_id) ids.add(it.articulo.id)
    return ids.size
  })
  const bloqueadoPorCodigos = !nvYaProcesada && checks.nv && codigosPendientes > 0

  const handleSubmit = () => {
    // INVARIANTE (Martin, 8 ago): Presea emite factura y remito JUNTOS — una
    // venta física no puede viajar sin elegir su remito de logística, o queda
    // mercadería facturada sin circuito de entrega (caso 90SPCL). El backend
    // tiene red de seguridad (lo crea solo + avisa), pero acá el operador
    // elige el tipo y la dirección conscientemente.
    if (checks.nv && !checks.remito && !yaTieneRemito && nv.requiere_entrega) {
      alert('Presea emite la factura y el remito JUNTOS: elegí el tipo de remito (entrega, planilla, retiro) antes de enviar — sin eso la mercadería queda facturada sin circuito de entrega.')
      return
    }
    if (checks.recibo) {
      // Guard: tarjeta/cheque sin N_BCO frenan en el guard del backend de Presea.
      const sinBco = pagos.find(p => {
        if (!p.medio_pago_id || !p.importe) return false
        const t = medios.find(m => String(m.id) === String(p.medio_pago_id))?.tipo
        return TIPOS_REQUIEREN_BANCO.has(t) && !p.n_bco
      })
      if (sinBco) {
        alert('Para tarjeta y cheque tenés que elegir la marca/banco real de Presea en la fila del pago — sino Presea rechaza el recibo.')
        return
      }
      const chequeSinFecha = pagos.find(p => p.medio_pago_id && p.importe
        && medios.find(m => String(m.id) === String(p.medio_pago_id))?.tipo === 'cheque' && !p.fecha_pago)
      if (chequeSinFecha) {
        alert('Cargá la fecha de pago del cheque.')
        return
      }
    }
    // Enriquecer cada recibo con `valores[]` cuando el operador eligió N_BCO real.
    // Acá tenemos `medios` para resolver el tipo del MedioPago → tipo_valor.
    const recibos = (!soloRemito && mostrarFormRecibo && checks.recibo)
      ? pagos.filter(p => p.medio_pago_id && p.importe).map(p => {
          const tipo = medios.find(m => String(m.id) === String(p.medio_pago_id))?.tipo
          // Efectivo no muestra selector → usa el N_BCO automático (EFECTIVO PESOS = 999).
          const nBcoFinal = p.n_bco || N_BCO_AUTO_POR_TIPO[tipo]
          const valores = (tipo && nBcoFinal) ? [{
            tipo_valor: tipo,
            n_bco: parseInt(nBcoFinal),
            importe: parseMonto(p.importe),
            cuotas: parseInt(p.cuotas) || 1,
            nro_cupon_cheque: p.nro_cupon_cheque || null,
            cod_autorizacion: p.cod_autorizacion || null,
            id_transaccion: p.id_transaccion || null,
            lote: p.lote || null,
            fecha_pago: FECHA_POR_MEDIO[tipo] ? (p.fecha_pago || null) : null,
          }] : undefined
          return { ...p, valores }
        })
      : []
    onConfirm({
      nv:      checks.nv,
      // Solo remito: el despacho va con FORMULARIO='REMITO' (sin factura ni
      // recibo) — la NV queda marcada sin_factura + sin_recibo.
      soloRemito,
      factura: false,
      remito90: checks.remito && tipoRemito === '90' ? { ...remito90, valor_declarado: valorDeclarado } : null,
      // Si el operador tildó "usar dirección de facturación" la dirección queda
      // como una copia del cliente → NO persistir en NV.direccion_envio (el
      // sistema seguirá leyendo del cliente y se mantiene sincronizado si el
      // cliente cambia de domicilio).
      usarDirFact,
      remito80: checks.remito && tipoRemito === '80',
      remitoParcial: checks.remito && tipoRemito === 'parcial' ? { ...remito90 } : null,
      remitoVirtual: checks.remito && tipoRemito === '0',
      remitoRetira: checks.remito && tipoRemito === 'retira',
      sucursalRetiro,
      // Si hay pagos vinculados (típico Magento), NO mandamos pagos manuales:
      // los confirmados los agarra el chain del backend (encolarCobrosPendientes)
      // tras procesar la NV. Solo se mandan recibos manuales si NO hay vinculados.
      recibos,
    })
  }

  const inp = { width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' }
  const lbl = { display: 'block', fontWeight: '600', fontSize: '0.8rem', color: '#374151', marginBottom: '0.2rem' }
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }

  return (
    <Modal title={nvYaProcesada ? `Agregar comprobantes — ${nv.numero_lgr}` : `Enviar paquete a Presea — ${nv.numero_lgr}`} onClose={onClose} maxWidth="520px">
      <BannerSinCodigo items={nv.items || []} onPendientes={setCodigosPendientes} />
      <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#475569' }}>
        Seleccioná qué comprobantes generar. Podés marcar más de uno y se procesan juntos.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>

        {/* NV */}
        {nvYaProcesada ? (
          <div style={{ ...envSt.block, background: '#f0fdf4', border: '1px solid #86efac' }}>
            <div style={{ ...envSt.checkLabel, cursor: 'default' }}>
              <span style={{ fontSize: '1rem' }}>✅</span>
              <span style={envSt.checkIcon}>🧾</span>
              <div>
                <strong>Nota de Venta</strong>
                <span style={{ ...envSt.hint, color: '#166534' }}>
                  Ya procesada — FAC {nv.comprobantes?.find(c => c.tipo === 'factura')?.numero_presea || nv.numero_presea}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div style={checks.nv ? envSt.blockActive : envSt.block}>
            <label style={envSt.checkLabel}>
              <input type="checkbox" checked={checks.nv} disabled={soloRemito} onChange={() => toggle('nv')} />
              <span style={envSt.checkIcon}>🧾</span>
              <div>
                <strong>Nota de Venta</strong>
                <span style={envSt.hint}>{soloRemito ? 'Viaja solo como vehículo del remito — SIN factura' : 'Procesa la NV y genera la factura'}</span>
              </div>
            </label>
          </div>
        )}

        {/* SOLO REMITO: mercadería sin factura ni recibo (traslado, reposición,
            muestra). Despacho con FORMULARIO='REMITO' — el modo original del WS.
            Con cobros vinculados no aplica (eso es una venta con recibo). */}
        {!nvYaProcesada && !yaTieneRemito && !hayPagosVinculados && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: '#0e7490', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={soloRemito} onChange={toggleSoloRemito} />
            🚚 <strong>Solo remito</strong>&nbsp;— enviar mercadería SIN factura ni recibo (traslado, reposición, muestra)
          </label>
        )}

        {/* Remito */}
        {yaTieneRemito ? (
          <div style={{ ...envSt.block, background: '#f0fdf4', border: '1px solid #86efac' }}>
            <div style={{ ...envSt.checkLabel, cursor: 'default' }}>
              <span style={{ fontSize: '1rem' }}>✅</span>
              <span style={envSt.checkIcon}>📦</span>
              <div>
                <strong>Remito</strong>
                <span style={{ ...envSt.hint, color: '#166534' }}>
                  Ya creado — {remitosVigentes[0]?.numero_presea || `ID ${remitosVigentes[0]?.id}`}
                </span>
              </div>
            </div>
          </div>
        ) : (
        <div style={checks.remito ? envSt.blockActive : envSt.block}>
          <label style={envSt.checkLabel}>
            <input type="checkbox" checked={checks.remito} disabled={soloRemito} onChange={() => toggle('remito')} />
            <span style={envSt.checkIcon}>📦</span>
            <div>
              {/* "Factura y Remito": Presea los emite JUNTOS (no hay factura
                  sola) — el título del bloque lo dice (pedido Martin 8 ago).
                  En modo Solo remito el mismo bloque elige el tipo de entrega. */}
              <strong>{soloRemito ? 'Remito (sin factura)' : 'Factura y Remito'}</strong>
              <span style={envSt.hint}>Elegí el tipo de entrega</span>
            </div>
          </label>

          {checks.remito && (
            <div style={envSt.subForm}>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <label style={envSt.radioLabel}>
                  <input type="radio" name="tipoRemito" value="90" checked={tipoRemito === '90'} onChange={() => setTipoRemito('90')} />
                  Transporte 90 — Entrega directa
                </label>
                <label style={envSt.radioLabel}>
                  <input type="radio" name="tipoRemito" value="80" checked={tipoRemito === '80'} onChange={() => setTipoRemito('80')} />
                  Transporte 80 — Notas de envío
                </label>
                <label style={envSt.radioLabel}>
                  <input type="radio" name="tipoRemito" value="parcial" checked={tipoRemito === 'parcial'} onChange={() => setTipoRemito('parcial')} />
                  📦 Envío parcial — despachar en partes
                </label>
                <label style={envSt.radioLabel}>
                  <input type="radio" name="tipoRemito" value="retira" checked={tipoRemito === 'retira'} onChange={() => setTipoRemito('retira')} />
                  🏬 Retira en depósito
                </label>
                <label style={envSt.radioLabel}>
                  <input type="radio" name="tipoRemito" value="0" checked={tipoRemito === '0'} onChange={() => setTipoRemito('0')} />
                  Sin entrega — Virtual
                </label>
              </div>
              {tipoRemito === '0' && (
                <div style={{ fontSize: '0.8125rem', color: '#0e7490', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, padding: '6px 10px', marginBottom: '0.5rem' }}>
                  ✨ Producto virtual (cena, evento, gift card): el remito se genera en Presea y se da por <strong>entregado automáticamente</strong> — no pasa por logística.
                </div>
              )}
              {tipoRemito === 'retira' && (
                <div style={{ fontSize: '0.8125rem', color: '#5b21b6', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: 8, padding: '6px 10px', marginBottom: '0.5rem' }}>
                  🏬 <strong>Retira en depósito</strong>: el remito queda para <strong>imprimir y confirmar</strong> (el papel impreso es el control de la preparación) y NO entra a logística. Se cierra desde el detalle del remito apretando "Marcar retirado" cuando el cliente pasa a buscarlo.
                  {vinoComoRetira && <div style={{ marginTop: 4, color: '#166534', fontWeight: 600 }}>📍 El cliente eligió retiro en la web.</div>}
                  {/* Dónde lo pasa a buscar: sale impreso en el remito para que
                      el depósito sepa en qué punto separar la mercadería. */}
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 3 }}>¿Dónde lo retira?</label>
                    <select
                      value={sucursalRetiro}
                      onChange={e => setSucursalRetiro(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #c4b5fd' }}
                    >
                      <option value="">— Sin especificar —</option>
                      {sucursales.map(s => (
                        <option key={s.id} value={s.id}>{s.nombre} — {s.direccion}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {tipoRemito === 'parcial' && (
                <div style={envSt.infoBox}>
                  📦 <strong>Envío parcial</strong>: la factura y el remito salen <strong>completos</strong> en Presea, y la mercadería se despacha <strong>de a partes</strong> desde el detalle del remito («🚚 Despachar parte»). Cada parte entra a logística como una nota de envío. La dirección de abajo es con la que nace cada parte, y en cada una se puede cambiar.
                </div>
              )}
              {tipoRemito === '90' || tipoRemito === 'parcial' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {tieneDirEnvio && (
                    <div style={{ fontSize: '0.8125rem', color: '#166534', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '6px 10px' }}>
                      📍 Dirección de <strong>envío</strong> del pedido autocompletada. Revisala y editá si hace falta.
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8125rem', color: '#1e40af', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={usarDirFact} onChange={e => handleUsarDirFact(e.target.checked)} />
                    Usar dirección de facturación del cliente
                  </label>
                  <div>
                    <label style={lbl}>Nombre / Razón social que recibe *</label>
                    <input style={inp} value={remito90.receptor_nombre} onChange={setR90('receptor_nombre')} placeholder="Nombre completo" />
                  </div>
                  <div>
                    <label style={lbl}>Teléfono de contacto</label>
                    <input style={inp} value={remito90.telefono} onChange={setR90('telefono')} placeholder="+54 11 1234-5678" />
                  </div>
                  <div>
                    <label style={lbl}>Domicilio (calle y número)</label>
                    <input
                      style={inp}
                      value={remito90.calle}
                      onChange={setR90('calle')}
                      placeholder="Ej: Av. Corrientes 1234"
                    />
                  </div>
                  <div>
                    {/* Campo PROPIO: escrito adentro de la calle se perdía. El
                        repartidor llega al edificio y sin esto no sabe a qué
                        puerta tocar. */}
                    <label style={lbl}>Piso / Depto</label>
                    <input
                      style={inp}
                      value={remito90.piso}
                      onChange={setR90('piso')}
                      placeholder="Ej: 3° B — o timbre, casa del fondo…"
                    />
                  </div>
                  <div style={row2}>
                    <div>
                      <label style={lbl}>Provincia *</label>
                      <select style={inp} value={remito90.provincia} onChange={setR90('provincia')}>
                        <option value="">— Seleccionar —</option>
                        {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    {!esCabaR && (
                      <div>
                        <label style={lbl}>Localidad</label>
                        {/* Texto con autocompletado y aprendizaje (sugerencia #72): el
                            select cerrado de LOCALIDADES_BA dejaba en blanco cualquier
                            localidad del interior de PBA — Carmen de Patagones se perdía
                            al armar el remito. */}
                        <CampoLocalidad
                          provincia={remito90.provincia}
                          value={remito90.localidad}
                          onChange={v => setRemito90(f => ({ ...f, localidad: v }))}
                          inputStyle={inp}
                          cp={remito90.codigo_postal}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={lbl}>Código postal{esCabaR ? ' *' : ''}</label>
                    <input style={inp} value={remito90.codigo_postal} onChange={onCpR90} />
                  </div>
                  {tipoRemito === '90' && (
                  <div>
                    {/* Sug #38: opcional, vacío por defecto — solo cuando
                        corresponde. Sale impreso en el remito; NO viaja a
                        Andreani (autoseguro propio). */}
                    <label style={lbl}>Valor declarado (opcional)</label>
                    <MoneyInput style={inp} value={valorDeclarado} onChange={setValorDeclarado} placeholder="" />
                  </div>
                  )}
                </div>
              ) : tipoRemito === '80' ? (
                <div style={envSt.infoBox}>
                  Al confirmar, serás redirigido a la sección de Remitos donde podés crear la planilla de distribución (múltiples destinatarios).
                </div>
              ) : null /* virtual y retira: el banner de arriba ya lo explica */}
            </div>
          )}
        </div>
        )}


        {/* Recibo — vinculados (read-only) vs manual (form). En modo Solo
            remito no hay recibo: la sección desaparece. */}
        {soloRemito ? null : hayPagosVinculados ? (
          <div style={{ ...envSt.block, background: pagosConfirmados.length > 0 ? '#f0fdf4' : '#fffbeb', border: pagosConfirmados.length > 0 ? '1px solid #86efac' : '1px solid #fcd34d' }}>
            <div style={{ ...envSt.checkLabel, cursor: 'default' }}>
              <span style={{ fontSize: '1rem' }}>{pagosConfirmados.length > 0 ? '✅' : '⚠️'}</span>
              <span style={envSt.checkIcon}>💰</span>
              <div style={{ flex: 1 }}>
                <strong>Recibo de cobranza</strong>
                <span style={{ ...envSt.hint, color: pagosConfirmados.length > 0 ? '#166534' : '#92400e' }}>
                  {pagosConfirmados.length > 0
                    ? `${pagosConfirmados.length} cobro(s) confirmado(s) — se incluyen en el paquete`
                    : `${pagosPendientes.length} cobro(s) sin confirmar — NO se incluyen. Confirmá en /recibos primero.`}
                </span>
              </div>
            </div>
            <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingLeft: '2.25rem' }}>
              {pagosVinculados.map(p => (
                <div key={p.id} style={{ fontSize: '0.8125rem', display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'center', color: '#374151' }}>
                  <span>
                    {p.medio_pago || 'Pago'} — ${fmtMonto(p.importe)}
                    {' · '}
                    <span style={{ fontWeight: 600, color: p.estado === 'confirmado' ? '#166534' : p.estado === 'pendiente' ? '#92400e' : '#64748b' }}>
                      {p.estado}
                    </span>
                  </span>
                  {p.numero_presea && <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: '0.75rem' }}>{p.numero_presea}</code>}
                </div>
              ))}

              {/* Falta plata: pasa cuando se le agregó un artículo al pedido
                  después de que el cliente pagó. Sin esto la factura sale con
                  saldo abierto y hay que perseguirlo por afuera. */}
              {faltaCobrar && (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #fcd34d', fontSize: '0.8125rem', color: '#92400e' }}>
                  <strong>Falta cobrar ${fmtMonto(saldoNv)}</strong>
                  {' — '}a cobrar ${fmtMonto(totalACobrar)}{percepEstimadas > 0 ? ' (con percepciones)' : ''} y hay ${fmtMonto(cobradoVigente)} cobrados.
                  Cargá abajo el recibo complementario.
                </div>
              )}
            </div>
          </div>
        ) : null}

        {mostrarFormRecibo && (
        <div style={checks.recibo ? envSt.blockActive : envSt.block}>
          <label style={envSt.checkLabel}>
            <input type="checkbox" checked={checks.recibo} onChange={() => {
              // Al tildarlo con saldo pendiente, el importe viene puesto: es el
              // que falta, y es el que se va a cargar en el 99% de los casos.
              if (!checks.recibo && faltaCobrar) {
                setPagos(prev => prev.length === 1 && !prev[0].importe
                  ? [{ ...prev[0], importe: String(saldoNv) }]
                  : prev)
              }
              toggle('recibo')
            }} />
            <span style={envSt.checkIcon}>💰</span>
            <div>
              <strong>{hayPagosVinculados ? 'Recibo complementario' : 'Recibo de cobranza'}</strong>
              <span style={envSt.hint}>
                {hayPagosVinculados
                  ? `Por los $${fmtMonto(saldoNv)} que faltan cobrar`
                  : 'Registra el pago del cliente'}
              </span>
            </div>
          </label>

          {checks.recibo && (
            <div style={envSt.subForm}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {pagos.map((p, idx) => {
                  const medioSel = medios.find(m => String(m.id) === String(p.medio_pago_id))
                  const tipoMedio = medioSel?.tipo || ''
                  const camposMedio = CAMPOS_MEDIO[tipoMedio] || []
                  const tipovalorBco = TIPOVALOR_POR_TIPO[tipoMedio]
                  const mostrarBco = TIPOS_PERMITEN_BANCO.has(tipoMedio)
                  const bcoRequerido = TIPOS_REQUIEREN_BANCO.has(tipoMedio)
                  const lblBco = tipoMedio === 'tarjeta' ? 'Marca / Pasarela'
                    : tipoMedio === 'cheque' ? 'Banco emisor'
                    : tipoMedio === 'transferencia' ? 'Cuenta destino'
                    : tipoMedio === 'mp' ? 'Pasarela'
                    : 'Banco'
                  return (
                    <div key={p._key} style={envSt.pagoFila}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                        <strong style={{ fontSize: '0.8125rem', color: '#1e293b' }}>Medio de pago {idx + 1}</strong>
                        {pagos.length > 1 && (
                          <Boton variante="destructive" sm onClick={() => quitarPago(idx)} type="button">Eliminar</Boton>
                        )}
                      </div>
                      <div style={row2}>
                        {FECHA_POR_MEDIO[tipoMedio] ? (
                          <div>
                            <label style={lbl}>
                              {FECHA_POR_MEDIO[tipoMedio]} {tipoMedio === 'cheque' ? '*' : <span style={{ color: '#94a3b8', fontWeight: 500 }}>(opcional)</span>}
                            </label>
                            <CampoFecha style={inp} value={p.fecha_pago} onChange={v => setPago(idx, 'fecha_pago', v)} required={tipoMedio === 'cheque'} />
                          </div>
                        ) : (
                          <div>
                            <label style={lbl}>Fecha</label>
                            {/* La cobranza se registra HOY: la fija el backend (pago #1129). */}
                            <div style={{ ...inp, background: '#f8fafc', color: '#475569' }}
                              title="La cobranza se registra con la fecha de hoy. Si es un cheque, elegí el medio y cargá su fecha de pago.">
                              {fmtFecha(hoyISO())}
                            </div>
                          </div>
                        )}
                        <div>
                          <label style={lbl}>
                            Importe *
                            {/* Tilde "completar restante": calcula el saldo del TOTAL NV − suma
                                de las OTRAS filas y lo carga acá. Útil cuando hay multi-medio:
                                cargás $40k tarjeta y tildás la 2da fila en efectivo → se llena con
                                lo que falte. Al destildar el importe queda libre para editar. */}
                            {(() => {
                              const sumaOtras = pagos.reduce((acc, pp, i) => i !== idx ? acc + (parseMonto(pp.importe) || 0) : acc, 0)
                              // Contra lo que RESTA cobrar (descuenta cobros ya
                              // vinculados), no contra el total entero de la NV.
                              const restante = Math.max(0, Math.max(0, saldoNv) - sumaOtras)
                              const cubreRestante = restante > 0 && Math.abs((parseMonto(p.importe) || 0) - restante) < 0.005
                              return (
                                <label style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 500, color: '#166534', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                  title={`Completar con el saldo restante ($${fmtMonto(restante)})`}>
                                  <input
                                    type="checkbox"
                                    checked={cubreRestante}
                                    onChange={() => setPago(idx, 'importe', cubreRestante ? '' : restante.toFixed(2))}
                                  />
                                  restante (${fmtMonto(restante)})
                                </label>
                              )
                            })()}
                          </label>
                          <input style={inp} type="text" inputMode="decimal" value={p.importe} onChange={e => setPago(idx, 'importe', e.target.value)} placeholder="0.00" />
                        </div>
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <label style={lbl}>Medio / Valor *</label>
                        <select style={inp} value={p.medio_pago_id} onChange={e => setPago(idx, 'medio_pago_id', e.target.value)}>
                          <option value="">— Seleccionar —</option>
                          {medios.map(m => <option key={m.id} value={m.id}>{m.descripcion}</option>)}
                        </select>
                      </div>
                      {mostrarBco && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={lbl}>
                            {lblBco} {bcoRequerido ? '*' : <span style={{ color: '#94a3b8', fontWeight: 500 }}>(opcional)</span>}
                          </label>
                          <SelectorBancoPresea
                            tipo={tipovalorBco}
                            value={p.n_bco}
                            onChange={sel => setBancoPago(idx, sel)}
                            required={bcoRequerido}
                            placeholder={bcoRequerido ? 'Elegí el código real de Presea…' : 'Opcional'}
                          />
                        </div>
                      )}
                      {camposMedio.includes('nro_cupon_cheque') && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={lbl}>{medioSel?.tipo === 'cheque' ? 'Nº cheque' : 'Nº cupón'}</label>
                          <input style={inp} value={p.nro_cupon_cheque} onChange={e => setPago(idx, 'nro_cupon_cheque', e.target.value)} />
                        </div>
                      )}
                      {camposMedio.includes('cod_autorizacion') && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={lbl}>Código autorización</label>
                          <input style={inp} value={p.cod_autorizacion} onChange={e => setPago(idx, 'cod_autorizacion', e.target.value)} />
                        </div>
                      )}
                      {camposMedio.includes('id_transaccion') && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={lbl}>ID transacción</label>
                          <input style={inp} value={p.id_transaccion} onChange={e => setPago(idx, 'id_transaccion', e.target.value)} />
                        </div>
                      )}
                      {camposMedio.includes('cuotas') && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={lbl}>Cuotas</label>
                          <input style={inp} type="text" inputMode="numeric" pattern="[0-9]*" value={p.cuotas} onChange={e => setPago(idx, 'cuotas', e.target.value)} />
                        </div>
                      )}
                      {camposMedio.includes('lote') && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <label style={lbl}>Lote</label>
                          <input style={inp} value={p.lote || ''} onChange={e => setPago(idx, 'lote', e.target.value)} maxLength={50} placeholder="Nº de lote del cupón" />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <Boton variante="secondary" sm type="button" style={{ marginTop: '0.625rem' }} onClick={agregarPago}>+ Agregar medio de pago</Boton>

              <div style={envSt.totalesRecibo}>
                {/* La referencia es lo que RESTA cobrar: total a cobrar (con
                    percepciones) menos lo ya cobrado en recibos vinculados.
                    Comparar contra el total pelado de renglones marcaba
                    "Excede/Falta" cuando el operador cobraba exactamente lo
                    que la pantalla le indicó (sug #53). */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span>Este recibo</span>
                  <strong>${fmtMonto(totalCobrado)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span>A cobrar{percepEstimadas > 0 ? ' (c/ percepciones)' : ''}{cobradoVigente > 0 ? ' — restante' : ''}</span>
                  <strong>${fmtMonto(Math.max(0, saldoNv))}</strong>
                </div>
                {cobradoVigente > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b' }}>
                    <span>Total ${fmtMonto(totalACobrar)} · ya cobrado</span>
                    <span>${fmtMonto(cobradoVigente)}</span>
                  </div>
                )}
                {Math.abs(totalCobrado - Math.max(0, saldoNv)) > 0.01 && (
                  <div style={{ fontSize: '0.75rem', color: totalCobrado < saldoNv ? '#92400e' : '#1e40af', fontWeight: 600, marginTop: '0.25rem' }}>
                    {totalCobrado < saldoNv ? `Falta cubrir $${fmtMonto(saldoNv - totalCobrado)}` : `Excede en $${fmtMonto(totalCobrado - Math.max(0, saldoNv))}`}
                  </div>
                )}
              </div>

              {/* En el camino nativo de LGR, cargar el recibo en este modal YA es
                  la confirmación (el operador tiene/verificó la plata) → nace
                  confirmado y viaja a Presea con la NV. El flujo pendiente →
                  confirmar aparte es solo para Magento (plata por verificar). */}
            </div>
          )}
        </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem', alignItems: 'center' }}>
        {bloqueadoPorCodigos && (
          <span style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, marginRight: 'auto' }}>
            ⚠ Completá los códigos Presea para poder enviar
          </span>
        )}
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton
          variante="primary"
          onClick={handleSubmit}
          disabled={enviando || ningunoSeleccionado || bloqueadoPorCodigos}
          title={bloqueadoPorCodigos ? 'Hay artículos sin código Presea — completalos en el aviso de arriba' : undefined}
        >
          {enviando ? 'Enviando...' : '📤 Enviar paquete a Presea'}
        </Boton>
      </div>
    </Modal>
  )
}

const CHIP_COLORS = {
  borrador:        { bg: '#f1f5f9', color: '#475569' },
  pendiente_envio: { bg: '#fef3c7', color: '#92400e' },
  enviado:         { bg: '#dbeafe', color: '#1e40af' },
  procesado:       { bg: '#dcfce7', color: '#166534' },
  frenado:         { bg: '#fee2e2', color: '#dc2626' },
  pendiente:       { bg: '#fef3c7', color: '#92400e' },
  ingresado:       { bg: '#f3e8ff', color: '#7c3aed' },
  asignado:        { bg: '#dbeafe', color: '#1e40af' },
  en_distribucion: { bg: '#f3e8ff', color: '#7c3aed' },
  entregado:       { bg: '#dcfce7', color: '#166534' },
  devuelto:        { bg: '#fee2e2', color: '#dc2626' },
  generada:        { bg: '#f1f5f9', color: '#475569' },
  en_camino:       { bg: '#f3e8ff', color: '#7c3aed' },
  anulado:         { bg: '#fee2e2', color: '#dc2626' },
  acreditado:      { bg: '#dcfce7', color: '#166534' },
  confirmado:      { bg: '#dcfce7', color: '#166534' },
  'cobro fallido': { bg: '#fee2e2', color: '#991b1b' },
}

function Chip({ label }) {
  const col = CHIP_COLORS[label] || { bg: '#f1f5f9', color: '#475569' }
  return (
    <span style={{ background: col.bg, color: col.color, padding: '2px 9px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '700', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {label?.replace(/_/g, ' ')}
    </span>
  )
}

// Estado por paso del stepper. Deriva el "estado visual" de cada uno de los 3
// pasos del chain (Factura → Remito → Recibo) combinando lo que ya llegó de
// Presea (derivarPasos) con el estado global del polling, para distinguir el
// paso "en curso" (el primero pendiente mientras corre) del resto pendiente.
//   hecho     → Presea respondió OK (✓ azul/verde + nº mono)
//   en_curso  → es el próximo a resolverse y el chain está corriendo (spinner)
//   error     → el chain rebotó (✕ rojo)
//   na        → el paso no aplica (— gris, ej. sin recibo)
//   pendiente → todavía no llegó (○ gris)
function estadoPaso(pasoRaw, esProximo, pollingEstado) {
  if (pasoRaw === 'listo') return 'hecho'
  if (pasoRaw === 'na') return 'na'
  if (pollingEstado === 'error' || pollingEstado === 'timeout') return 'error'
  if (pollingEstado === 'corriendo' && esProximo) return 'en_curso'
  return 'pendiente'
}

// Lo que le falta a ESTE pedido para cerrarse, con el botón para cerrarlo.
//
// El motor de resolución deja casos "esperando_humano" cuyas instrucciones
// terminan en «tocá ✓ Verificar». Ese botón vivía sólo en SISTEMA → ERP Presea
// → Resolución, que es admin: el 04/08/2026 el sistema le pidió a un vendedor
// que verificara una nota de crédito, la emitió en Presea y se quedó sin dónde
// completarla, con la venta a medio camino. Un sistema no puede pedir algo que
// la persona no tiene manera de hacer.
//
// El gate NO se relaja: el número de la NC se busca en la query DETALLE_COMPRO
// de Presea y sólo con ese OK se completa el lado LGR. Cambia quién puede
// terminar su propio trabajo, no qué se da por cierto.
function PendienteEnPreseaCard({ nvId, onResuelto }) {
  const [pendiente, setPendiente] = useState(null)
  const [numeroNc, setNumeroNc] = useState('')
  const [verificando, setVerificando] = useState(false)
  const [aviso, setAviso] = useState(null)

  useEffect(() => {
    api.get(`/notas-venta/${nvId}/pendiente-presea`)
      .then(({ data }) => setPendiente(data.pendiente || null))
      .catch(() => setPendiente(null))
  }, [nvId])

  if (!pendiente) return null

  const verificar = () => {
    setVerificando(true)
    setAviso(null)
    api.post(`/notas-venta/${nvId}/verificar-presea`, { numero_nc: numeroNc.trim() || undefined })
      .then(({ data }) => {
        setAviso({ ok: true, texto: data.mensaje || 'Verificado en Presea.' })
        setPendiente(null)
        onResuelto?.()
      })
      .catch((e) => setAviso({
        ok: false,
        // El motor explica POR QUÉ no pudo (no aparece todavía, es de otro
        // cliente, el importe no coincide). Ese texto es el que sirve.
        texto: e.response?.data?.mensaje || 'No se pudo verificar contra Presea.',
      }))
      .finally(() => setVerificando(false))
  }

  return (
    <div style={{
      margin: '0 0 1rem',
      padding: '0.9rem 1.1rem',
      background: '#fffbeb',
      border: '1px solid #fcd34d',
      borderRadius: '8px',
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#92400e' }}>
        ⏳ Este pedido tiene algo pendiente para cerrarse
      </div>
      {pendiente.instrucciones && (
        <pre style={{
          margin: '0.5rem 0 0',
          fontSize: '0.78rem',
          lineHeight: 1.5,
          color: '#78350f',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit',
        }}>{pendiente.instrucciones}</pre>
      )}

      {pendiente.pide_numero_nc && (
        <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            value={numeroNc}
            onChange={e => setNumeroNc(e.target.value)}
            placeholder="Nº de la NC o devolución en Presea (varias: separadas por coma)"
            style={{
              flex: '1 1 260px',
              padding: '0.4rem 0.6rem',
              border: '1px solid #d97706',
              borderRadius: '5px',
              fontSize: '0.82rem',
              fontFamily: 'ui-monospace, monospace',
            }}
          />
          <Boton variante="success" sm onClick={verificar} disabled={verificando}>
            {verificando ? 'Verificando…' : '✓ Verificar en Presea'}
          </Boton>
        </div>
      )}

      {aviso && (
        <div style={{
          marginTop: '0.6rem',
          fontSize: '0.78rem',
          color: aviso.ok ? '#166534' : '#b91c1c',
        }}>
          {aviso.ok ? '✓ ' : '✕ '}{aviso.texto}
        </div>
      )}
    </div>
  )
}

// Cliente ambiguo (matching Magento con email en varias fichas): banner con
// las dos salidas — confirmar el elegido por el sistema o cambiarlo (patrón
// estándar ModalListaClientes). Mientras el flag esté alto, /enviar rebota.
function ClienteAmbiguoBanner({ nv, onResuelto }) {
  const [picker, setPicker] = useState(false)
  const [trabajando, setTrabajando] = useState(false)

  const resolver = async (clienteId) => {
    setTrabajando(true)
    try {
      await notasVentaApi.cambiarCliente(nv.id, clienteId)
      setPicker(false)
      onResuelto()
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo cambiar el cliente.')
    } finally { setTrabajando(false) }
  }

  return (
    <div style={{
      background: colors.warnBg, border: `1px solid ${colors.warn}55`, borderRadius: radius.md,
      padding: `${space.lg}px ${space.xl}px`, margin: `${space.lg}px 0`,
      display: 'flex', alignItems: 'center', gap: space.lg, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: text.body, color: colors.text }}>
        {nv.cliente_ambiguo_motivo ? (
          <>
            ⚠ <strong>Cliente frenado:</strong> {nv.cliente_ambiguo_motivo}{' '}
            La NV no viaja a Presea hasta que lo definas (ficha actual: <strong>{nv.cliente?.nombre}</strong>).
          </>
        ) : (
          <>
            ⚠ <strong>Cliente ambiguo:</strong> el email del pedido está en varias fichas.
            El envío a Presea queda frenado hasta que confirmes o cambies el cliente
            (elegido: <strong>{nv.cliente?.nombre}</strong>).
          </>
        )}
      </span>
      <span style={{ flex: 1 }} />
      <Boton variante="success" sm disabled={trabajando} onClick={() => resolver(nv.cliente.id)}>✓ Confirmar este cliente</Boton>
      <Boton variante="secondary" sm disabled={trabajando} onClick={() => setPicker(true)}>Cambiar cliente</Boton>
      {picker && <ModalListaClientes onSelect={(c) => resolver(c.id)} onClose={() => setPicker(false)} />}
    </div>
  )
}

// Los datos de este cliente ya están en otra ficha de la base (sugerencia #123;
// Martin, 27 oct 2026: "si el sistema encontró algún dato del cliente en la
// base se los sugiere, sencillo y claro"). Coincide por documento, email o
// teléfono exactos — nunca por nombre — y solo fichas de Presea. No frena nada:
// en azul, como la sugerencia de razón social.
const DATO_COINCIDE = { documento: 'documento', email: 'email', telefono: 'teléfono' }

function CoincidenciasClienteBanner({ nv, onCambiado }) {
  const [oculto, setOculto] = useState(false)
  const [trabajando, setTrabajando] = useState(false)
  if (oculto) return null

  const usar = async (clienteId) => {
    setTrabajando(true)
    try {
      await notasVentaApi.cambiarCliente(nv.id, clienteId, { coincidencia: true })
      onCambiado()
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo cambiar el cliente.')
    } finally { setTrabajando(false) }
  }

  const lista = (xs) => xs.length > 1 ? `${xs.slice(0, -1).join(', ')} y ${xs.at(-1)}` : xs[0]

  return (
    <div style={{
      background: colors.infoBg, border: `1px solid ${colors.info}33`, borderRadius: radius.md,
      padding: `${space.lg}px ${space.xl}px`, margin: `${space.lg}px 0`,
    }}>
      <div style={{ fontSize: text.body, color: colors.text, marginBottom: space.md }}>
        💡 <strong>Este cliente ya está en la base.</strong> Los datos de{' '}
        <strong>{nv.cliente?.nombre}</strong> coinciden con{' '}
        {nv.clientes_coincidentes.length === 1 ? 'otro cliente' : `${nv.clientes_coincidentes.length} clientes`}.
        ¿Facturamos a alguno de ellos?
      </div>
      {nv.clientes_coincidentes.map(c => (
        <div key={c.id} style={{
          display: 'flex', alignItems: 'center', gap: space.lg, flexWrap: 'wrap',
          padding: `${space.sm}px 0`, borderTop: `1px solid ${colors.info}22`,
        }}>
          <span style={{ fontSize: text.body, color: colors.text, flex: 1, minWidth: 260 }}>
            <span style={{ fontFamily: font.mono }}>{c.codigo_presea}</span> <strong>{c.nombre}</strong>
            <span style={{ color: colors.textSec }}>
              {' · '}mismo {lista(c.coincide.map(m => DATO_COINCIDE[m] || m))}
              {' · '}{c.compras > 0
                ? `${c.compras} ${c.compras === 1 ? 'compra' : 'compras'}, la última el ${fmtFecha(c.ultima_compra)}`
                : 'sin compras'}
            </span>
          </span>
          <Boton variante="primary" sm disabled={trabajando} onClick={() => usar(c.id)}>
            Facturar a {c.codigo_presea}
          </Boton>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: space.sm }}>
        <Boton variante="ghost" sm disabled={trabajando} onClick={() => setOculto(true)}>Dejar como está</Boton>
      </div>
    </div>
  )
}

// Sugerencia de razón social: este comprador ya facturó antes a nombre de otra
// ficha (típico: compra con su usuario web, factura a su empresa). El vínculo lo
// aprendió el sistema de correcciones anteriores que terminaron facturadas.
// SUGIERE, no aplica: en azul (info), no en amarillo, porque no frena nada.
function SugerenciaFacturacionBanner({ nv, onCambiado }) {
  const [oculto, setOculto] = useState(false)
  const [trabajando, setTrabajando] = useState(false)

  const opciones = nv.sugerencia_facturacion?.opciones || []
  if (oculto || opciones.length === 0) return null

  const usar = async (clienteId) => {
    setTrabajando(true)
    try {
      await notasVentaApi.cambiarCliente(nv.id, clienteId)
      onCambiado()
    } catch (e) {
      alert(e.response?.data?.message || 'No se pudo cambiar la razón social.')
    } finally { setTrabajando(false) }
  }

  const [principal, ...otras] = opciones

  return (
    <div style={{
      background: colors.infoBg, border: `1px solid ${colors.info}33`, borderRadius: radius.md,
      padding: `${space.lg}px ${space.xl}px`, margin: `${space.lg}px 0`,
      display: 'flex', alignItems: 'center', gap: space.lg, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: text.body, color: colors.text }}>
        💡 <strong>{nv.sugerencia_facturacion.comprador}</strong> ya facturó antes como{' '}
        <strong>{principal.nombre}</strong>
        {principal.veces > 1 && ` (${principal.veces} veces)`}. ¿Facturar a esa razón social?
      </span>
      <span style={{ flex: 1 }} />
      <Boton variante="primary" sm disabled={trabajando} onClick={() => usar(principal.cliente_id)}>
        Facturar a {principal.nombre}
      </Boton>
      {otras.map(o => (
        <Boton key={o.cliente_id} variante="secondary" sm disabled={trabajando} onClick={() => usar(o.cliente_id)}>
          {o.nombre}
        </Boton>
      ))}
      <Boton variante="ghost" sm disabled={trabajando} onClick={() => setOculto(true)}>Dejar como está</Boton>
    </div>
  )
}

// Banner de progreso del chain Presea (Factura → Remito → Recibo). Visible
// arriba de la cadena mientras dura el polling y unos segundos al final.
// Rediseñado como stepper/timeline con la paleta del sistema (slate + azul);
// sin amarillo: "en curso" = azul con spinner sutil, "hecho" = verde, "error"
// = rojo (tokens colors.*).
function BannerProgresoEnvio({ polling, nv, onCerrar }) {
  if (polling.estado === 'idle') return null
  const pasos = derivarPasos(nv)
  const reciboNum = (nv?.pagos || []).find(p => p.numero_presea)?.numero_presea

  // Solo recibo: la NV ya estaba en Presea y se agregó un recibo.
  if (polling.pagoIds) {
    const nuevos = (nv?.pagos || []).filter(p => polling.pagoIds.includes(p.id))
    const listo = nuevos.length === polling.pagoIds.length && nuevos.every(p => p.numero_presea)
    const item = {
      key: 'recibo', label: polling.pagoIds.length > 1 ? 'Recibos' : 'Recibo',
      num: nuevos.map(p => p.numero_presea).filter(Boolean).join(' · '), fallback: '',
      st: listo ? 'hecho' : estadoPaso('pendiente', true, polling.estado),
    }
    const head = polling.estado === 'completo'
        ? { titulo: 'Recibo generado en Presea', sub: null, acento: colors.ok }
      : polling.estado === 'timeout'
        ? { titulo: 'El recibo está tardando', sub: polling.error, acento: colors.info }
      : { titulo: 'Enviando el recibo a Presea', sub: 'Esperando el número de recibo — no hace falta hacer nada.', acento: colors.link }
    return <MarcoBannerProgreso head={head} items={[item]} cerrable={['completo', 'timeout'].includes(polling.estado)} onCerrar={onCerrar} />
  }

  // El "próximo paso" es el primer pendiente en el orden Factura→Remito→Recibo:
  // sobre él se muestra el spinner mientras el chain corre.
  const proximoPendiente = pasos.factura !== 'listo' ? 'factura'
    : pasos.remito !== 'listo' ? 'remito'
    : (pasos.recibo !== 'listo' && pasos.recibo !== 'na') ? 'recibo'
    : null

  const items = [
    { key: 'factura', label: 'Factura', num: nv?.numero_presea, fallback: '' },
    { key: 'remito',  label: 'Remito',  num: nv?.numero_remito_presea, fallback: '' },
    { key: 'recibo',  label: 'Recibo',  num: reciboNum, fallback: pasos.recibo === 'na' ? 'Sin recibo' : '' },
  ].map(it => ({ ...it, st: estadoPaso(pasos[it.key], proximoPendiente === it.key, polling.estado) }))

  // Encabezado del banner según el estado global.
  const head = polling.estado === 'completo'
      ? { titulo: 'Paquete completo', sub: 'Factura, remito y recibo generados en Presea.', acento: colors.ok }
    : polling.estado === 'error'
      ? { titulo: 'El paquete fue rechazado', sub: polling.error, acento: colors.err }
    : polling.estado === 'timeout'
      ? { titulo: 'El chain está tardando', sub: polling.error, acento: colors.info }
    : { titulo: 'Procesando paquete en Presea', sub: 'Generando los comprobantes uno a uno…', acento: colors.link }

  const cerrable = ['completo', 'error', 'timeout'].includes(polling.estado)

  return <MarcoBannerProgreso head={head} items={items} cerrable={cerrable} onCerrar={onCerrar} />
}

// Marco del banner de progreso: encabezado + stepper. Lo comparten el
// paquete completo y el recibo suelto sobre una NV ya en Presea.
function MarcoBannerProgreso({ head, items, cerrable, onCerrar }) {
  return (
    <div style={{
      ...sx.card,
      padding: 0,
      marginBottom: space.xl,
      overflow: 'hidden',
      // Filete de acento a la izquierda — color según el estado, sin fondos chillones.
      borderLeft: `3px solid ${head.acento}`,
    }}>
      <style>{`
        @keyframes lgr-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Encabezado */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: space.lg,
        padding: `${space.lg}px ${space.xl}px`,
        borderBottom: `1px solid ${colors.borderSoft}`,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: font.sans, fontSize: text.body, fontWeight: 700, color: colors.text }}>
            {head.titulo}
          </div>
          {head.sub && (
            <div style={{ fontFamily: font.sans, fontSize: text.sec, color: colors.textSec, marginTop: 2 }}>
              {head.sub}
            </div>
          )}
        </div>
        {cerrable && (
          <button
            onClick={onCerrar}
            title="Cerrar"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: text.title, lineHeight: 1, color: colors.textMeta, padding: `0 ${space.xs}px`,
            }}
          >×</button>
        )}
      </div>

      {/* Stepper horizontal: Factura → Remito → Recibo */}
      <div style={{
        display: 'flex', alignItems: 'stretch',
        padding: `${space.lg}px ${space.xl}px`,
        gap: 0,
      }}>
        {items.map((it, i) => (
          <PasoChain key={it.key} item={it} primero={i === 0} />
        ))}
      </div>
    </div>
  )
}

// Un paso del stepper del chain: marcador (✓ / spinner / ○ / ✕ / —) + label
// sans + nº de comprobante mono cuando ya existe. El conector hereda el color
// del paso anterior.
function PasoChain({ item, primero }) {
  const { st } = item
  const tono = st === 'hecho' ? colors.ok
    : st === 'en_curso' ? colors.link
    : st === 'error' ? colors.err
    : colors.textMeta // pendiente / na

  const marcador = st === 'hecho' ? (
    <span style={dotBase(colors.okBg, colors.ok)}>✓</span>
  ) : st === 'en_curso' ? (
    <span style={{
      ...dotBase(colors.linkBg, colors.link),
      borderTopColor: 'transparent',
      animation: 'lgr-spin 0.8s linear infinite',
    }} />
  ) : st === 'error' ? (
    <span style={dotBase(colors.errBg, colors.err)}>✕</span>
  ) : st === 'na' ? (
    <span style={dotBase(colors.bgSoft, colors.textMeta)}>—</span>
  ) : (
    <span style={dotBase(colors.bgSoft, colors.textPlh)}>○</span>
  )

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
      {/* Conector desde el paso anterior */}
      {!primero && (
        <div style={{
          flex: 1, height: 2, margin: `0 ${space.sm}px`,
          background: st === 'hecho' || st === 'na' ? colors.border : colors.borderSoft,
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, minWidth: 0 }}>
        {marcador}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...sx.cardTitle, color: tono }}>{item.label}</div>
          <div style={{
            fontFamily: item.num ? font.mono : font.sans,
            fontVariantNumeric: item.num ? 'tabular-nums' : 'normal',
            fontSize: text.sec, fontWeight: item.num ? 600 : 500,
            color: item.num ? colors.text : colors.textMeta,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            marginTop: 1,
          }}>
            {item.num
              ? item.num
              : item.fallback
                ? item.fallback
                : st === 'en_curso' ? 'Procesando…'
                : st === 'error' ? 'Rechazado'
                : 'En espera'}
          </div>
        </div>
      </div>
    </div>
  )
}

// Marcador circular del stepper (24px). bg suave + texto del color del estado.
function dotBase(bg, fg) {
  return {
    flexShrink: 0,
    width: 22, height: 22, borderRadius: radius.pill,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: bg, color: fg, border: `2px solid ${fg}`,
    fontSize: 12, fontWeight: 700, boxSizing: 'border-box',
  }
}

function CadenaComprobantes({ nv, onNavigate }) {
  let facturas  = nv.comprobantes?.filter(c => c.tipo === 'factura') || []
  const ncs       = nv.comprobantes?.filter(c => c.tipo === 'nc' || c.tipo === 'nota_credito') || []
  // Remitos: solo los que NO están anulados. Un remito anulado significa que
  // el chain Presea falló antes de tener factura y se limpió el placeholder.
  let remitos   = (nv.remitos || []).filter(r => r.estado !== 'anulado')
  // Pagos: anulados/rechazados/devueltos no son comprobantes vigentes.
  // El cobro fallido de una membresía se ve (en rojo) aunque esté rechazado: LGR lo reintenta.
  const pagos     = (nv.pagos || []).filter(p => p.cobro_fallido || !['anulado', 'rechazado', 'devuelto'].includes(p.estado))

  // Estado del envío. SIN factura Presea no se muestra remito ni recibo —
  // la cadena espera la confirmación de Presea (regla Martin 18 jun: paso 1
  // factura, paso 2 remito o recibo. Sin factura no hay nada).
  const procesandoEnvio = ['pendiente_envio', 'enviado'].includes(nv.estado) && !nv.numero_presea
  const tieneFacturaPresea = Boolean(nv.numero_presea)

  // Fallback: cuando la NV se procesó pero no hay objeto Comprobante/Remito en
  // LGR (caso scripts/jobs que solo sincronizaron los números fiscales),
  // mostramos los strings de notas_venta para que la cadena no se vea vacía.
  if (facturas.length === 0 && tieneFacturaPresea) {
    facturas = [{ numero_presea: nv.numero_presea, tipo: 'factura', url: null }]
  }
  if (remitos.length === 0 && nv.numero_remito_presea && (tieneFacturaPresea || nv.sin_factura)) {
    remitos = [{ id: null, numero_presea: nv.numero_remito_presea, estado: 'procesado', transporte: null, zona: null, notas_envio: [] }]
  }
  // Si todavía no hay factura confirmada por Presea, ocultamos remitos
  // (placeholders R000xxx aún sin confirmación) — la cadena no pinta remito
  // sin factura. EXCEPCIÓN: solo remito (sin_factura) — ahí el remito ES la
  // operación y se muestra sin factura.
  if (!tieneFacturaPresea && !nv.sin_factura) {
    remitos = []
  }

  const PASOS = [
    {
      key: 'nv',
      label: 'Nota de Venta',
      icon: '🧾',
      items: [{
        id: nv.id,
        titulo: nv.numero_lgr || `#${nv.id}`,
        subtitulo: nv.numero_presea ? `Presea ${nv.numero_presea}` : null,
        chip: nv.estado,
        onClick: null,
      }],
    },
    {
      key: 'fac',
      label: 'Factura',
      icon: '📄',
      // Si todavía no hay factura pero el envío está en curso, mostramos
      // "Procesando…" como item activo (sin link). Cuando Presea responde,
      // el componente se refresca solo y aparece el nº real.
      items: procesandoEnvio
        ? [{ id: 'procesando-fac', titulo: 'Procesando…', subtitulo: 'Esperando confirmación de Presea', chip: nv.estado, onClick: null, href: null }]
        : facturas.map(c => ({
          id: c.numero_presea,
          titulo: `Nº ${c.numero_presea}`,
          subtitulo: null,
          chip: null,
          // Si el comprobante trae URL externa (Presea PDF), respetarla; si no,
          // descargar el impreso fiscal propio (pdf/factura-fiscal con datos
          // reales de DETALLE_COMPRO).
          onClick: c.url
            ? null
            : () => descargarArchivo(`/notas-venta/${nv.id}/factura-fiscal`, `factura_${c.numero_presea}.pdf`),
          href: c.url || null,
          linkLabel: 'Ver PDF',
        })),
      pendiente: nv.sin_factura ? 'No lleva factura (solo remito)' : 'No facturada',
    },
    {
      key: 'nc',
      label: 'Nota de Crédito',
      icon: '↩️',
      items: ncs.map(c => ({
        id: c.numero_presea,
        titulo: `Nº ${c.numero_presea}`,
        subtitulo: null,
        chip: null,
        href: c.url || null,
        linkLabel: 'Ver PDF',
      })),
      pendiente: 'Sin nota de crédito',
      optional: true,
    },
    {
      key: 'rem',
      label: 'Remito',
      icon: '📦',
      items: remitos.map(r => ({
        id: r.id,
        titulo: r.numero_presea ? `Nº ${r.numero_presea}` : `ID ${r.id}`,
        // Ojo: cuando el remito lo emitió Presea y LGR todavía no tiene el suyo,
        // acá llega un remito de relleno SIN transporte. Sin este guard se
        // imprimía "Tnull" en pantalla.
        subtitulo: [r.zona, r.transporte === 0 ? 'Virtual' : (r.transporte == null ? null : `T${r.transporte}`)].filter(Boolean).join(' · '),
        chip: r.estado,
        // El item de fallback (número fiscal sin objeto Remito en LGR) no tiene
        // a dónde ir: sin id el link armaba /remitos/null y el backend reventaba.
        onClick: r.id ? () => onNavigate(`/remitos/${r.id}`) : null,
        linkLabel: r.id ? 'Ver remito' : null,
        hijos: r.notas_envio?.map(ne => ({
          id: ne.id,
          titulo: `NE ${ne.numero_ne || ne.id}`,
          subtitulo: [ne.destinatario, ne.fecha_entrega_est ? fmtFecha(ne.fecha_entrega_est) : null].filter(Boolean).join(' · '),
          chip: ne.estado,
        })) || [],
      })),
      pendiente: 'Sin remito generado',
    },
    {
      key: 'rec',
      label: 'Recibo',
      icon: '💰',
      items: pagos.map(p => {
        // Aviso explícito: si la NV ya está facturada por Presea y el cobro
        // está confirmado pero NO tiene numero_presea, el recibo NO se llegó
        // a generar en Presea — el operador necesita saberlo (regla Martin:
        // si el recibo no se pudo imprimir, dar aviso).
        const reciboPendiente = tieneFacturaPresea && p.estado === 'confirmado' && !p.numero_presea
        // Aviso de medio inválido para Presea: si el cobro viene con un medio
        // de pasarela (Mobbex / SugaPay) — esos no son métodos válidos en
        // BANCOS de Presea (no tienen N_BCO). El operador tiene que entrar al
        // recibo y editarlo poniendo el método real (VISA, MASTERCARD, etc).
        // (Regla Martin 19 jun: la plata SÍ entró → estado verde, pero el
        // medio hay que reemplazarlo antes de enviar a Presea.)
        const revisarMedio = p.medio_pago && MEDIOS_PASARELA_REVISAR.has(String(p.medio_pago).toLowerCase())
        return {
          id: p.id,
          titulo: p.numero_presea ? `Nº ${p.numero_presea}` : `ID ${p.id}`,
          subtitulo: [
            fmtFecha(p.fecha),
            `$${fmtMonto(p.importe || 0)}`,
            p.medio_pago,
            p.cobro_fallido ? 'La pasarela no pudo cobrarlo: LGR lo reintenta y, cuando entra, pasa a confirmado solo' : null,
            reciboPendiente ? '⚠ Sin recibo Presea — reintentar envío' : null,
            revisarMedio ? '⚠ Editar y poner método real (Mobbex/SugaPay no se factura en Presea)' : null,
          ].filter(Boolean).join(' · '),
          chip: p.a_devolver ? 'a devolver' : p.cobro_fallido ? 'cobro fallido' : p.estado,
          onClick: () => onNavigate(`/pagos/${p.id}`),
          linkLabel: 'Ver recibo',
        }
      }),
      // Marcada "sin recibo" (canje MKT / prueba): no se espera cobro — el
      // paso lo dice en vez de quedar como deuda eterna.
      pendiente: nv.sin_recibo
        ? `No lleva recibo — ${nv.sin_recibo_motivo || 'sin cobro'} (operación completa)`
        : 'Sin recibo registrado',
    },
  ]

  return (
    <div style={styles.card}>
      <h3 style={styles.cardTitle}>Comprobantes de la operación</h3>
      <div style={cSt.pipeline}>
        {PASOS.map((paso, pi) => {
          const tieneItems = paso.items.length > 0
          const esOpcional = paso.optional && !tieneItems
          if (esOpcional) return null

          return (
            <div key={paso.key} style={cSt.paso}>
              {/* Connector line */}
              {pi > 0 && <div style={cSt.connector} />}

              {/* Step header */}
              <div style={cSt.pasoHeader}>
                <span style={{ ...cSt.dot, background: tieneItems ? '#1e40af' : '#d1d5db' }} />
                <span style={{ ...cSt.pasoLabel, color: tieneItems ? '#1e293b' : '#94a3b8' }}>
                  {paso.icon} {paso.label}
                </span>
              </div>

              {/* Items or pending */}
              <div style={cSt.pasoBody}>
                {tieneItems ? paso.items.map(item => (
                  <div key={item.id} style={cSt.itemCard}>
                    <div style={cSt.itemRow}>
                      {item.onClick ? (
                        <button style={cSt.itemLink} onClick={item.onClick}>
                          {item.titulo} →
                        </button>
                      ) : item.href ? (
                        <a href={item.href} target="_blank" rel="noreferrer" style={cSt.itemLinkA}>
                          {item.titulo} →
                        </a>
                      ) : (
                        <span style={cSt.itemTitulo}>{item.titulo}</span>
                      )}
                      {item.chip && <Chip label={item.chip} />}
                    </div>
                    {item.subtitulo && <div style={cSt.itemSub}>{item.subtitulo}</div>}
                    {item.hijos?.map(h => (
                      <div key={h.id} style={cSt.hijoRow}>
                        <span style={cSt.hijoBullet}>↳</span>
                        <span style={cSt.hijoTitulo}>{h.titulo}</span>
                        {h.subtitulo && <span style={cSt.itemSub}>{h.subtitulo}</span>}
                        {h.chip && <Chip label={h.chip} />}
                      </div>
                    ))}
                  </div>
                )) : (
                  <div style={cSt.pendiente}>{paso.pendiente}</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const cSt = {
  pipeline: { display: 'flex', flexDirection: 'column' },
  paso: { display: 'flex', flexDirection: 'column' },
  connector: { width: '2px', height: '12px', background: '#e2e8f0', marginLeft: '7px' },
  pasoHeader: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' },
  dot: { width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0 },
  pasoLabel: { fontWeight: '700', fontSize: '0.8125rem', letterSpacing: '0.01em' },
  pasoBody: { marginLeft: '24px', marginBottom: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' },
  itemCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '0.5rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  itemRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  itemTitulo: { fontWeight: '700', fontSize: '0.9rem', color: '#1e293b', fontFamily: 'monospace' },
  itemLink: { background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'monospace', textDecoration: 'underline', textUnderlineOffset: '2px' },
  itemLinkA: { color: '#1d4ed8', fontWeight: '700', fontSize: '0.9rem', fontFamily: 'monospace', textDecoration: 'underline', textUnderlineOffset: '2px' },
  itemSub: { fontSize: '0.8rem', color: '#64748b' },
  pendiente: { fontSize: '0.8125rem', color: '#94a3b8', fontStyle: 'italic', paddingLeft: '0.25rem' },
  hijoRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.25rem', borderTop: '1px solid #e2e8f0', marginTop: '0.125rem' },
  hijoBullet: { color: '#94a3b8', fontSize: '0.75rem', flexShrink: 0 },
  hijoTitulo: { fontWeight: '600', fontSize: '0.8125rem', color: '#334155', fontFamily: 'monospace' },
}

const styles = {
  fceAviso: { margin: '0 0 1rem', padding: '12px 16px', background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, borderRadius: 8, color: colors.warn, fontSize: '0.9rem', lineHeight: 1.45 },
  fceEmitida: { margin: '0 0 1rem', padding: '10px 16px', background: colors.infoSoft, border: `1px solid ${colors.infoBorder}`, borderRadius: 8, color: colors.info, fontSize: '0.88rem' },
  wrap: { maxWidth: '1100px' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' },
  title: { margin: '0.25rem 0 0', fontSize: '1.25rem', color: '#1e293b' },
  presea: { fontSize: '0.8125rem', color: '#64748b', fontFamily: 'monospace', marginLeft: '0.5rem' },
  preseaLink: { color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer' },
  // "Facturado en Presea a:" — bloque debajo de los números, con tinte ámbar
  // cuando Presea facturó a un cliente DISTINTO al de LGR (típico Magento sin
  // CUIT → CONSUMIDOR FINAL). El operador lo ve de un golpe.
  cliPrea: { fontSize: '0.8125rem', color: '#475569', marginTop: '0.4rem' },
  cliPreseaDiff: { fontSize: '0.8125rem', color: '#92400e', marginTop: '0.4rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px', display: 'inline-block' },
  cliPreseaWarn: { fontWeight: 600 },
  preseaCodInline: { color: '#94a3b8', fontFamily: 'monospace' },
  acciones: { display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' },
  success: { background: '#dcfce7', color: '#166534', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontWeight: '600' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1rem', marginBottom: '1rem' },
  card: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: '1rem' },
  cardTitle: { margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: '700', color: '#1e293b' },
  infoGrid: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'center', fontSize: '0.875rem' },
  infoLabel: { fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' },
  envioPanel: { marginTop: '1rem', border: '1px solid #e2e8f0', borderLeft: '3px solid #94a3b8', borderRadius: '6px', padding: '0.75rem 0.9rem', background: '#f8fafc' },
  envioHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' },
  envioHeader: { fontSize: '0.72rem', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' },
  envioMapa: { fontSize: '0.78rem', fontWeight: '600', color: '#1d4ed8', textDecoration: 'none' },
  mailLink: { color: '#1d4ed8', textDecoration: 'none' },
  envioGrid: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.35rem 0.9rem', alignItems: 'baseline', fontSize: '0.875rem' },
  envioLabel: { fontSize: '0.72rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' },
  envioVal: { color: '#1e293b' },
  envioMono: { color: '#1e293b', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' },
  envioNota: { fontSize: '0.85rem', color: '#475569', lineHeight: '1.4' },
  historial: { display: 'flex', flexDirection: 'column', gap: '0.625rem' },
  historialItem: { background: '#f8fafc', borderRadius: '6px', padding: '0.625rem 0.75rem' },
  historialEstados: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '4px' },
  estadoViejo: { fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' },
  flecha: { color: '#94a3b8', fontSize: '0.75rem' },
  historialMeta: { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8' },
  historialMotivo: { marginTop: '4px', fontSize: '0.75rem', color: '#475569' },
  emptyText: { color: '#94a3b8', fontSize: '0.875rem' },
  itemTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  itemTh: { padding: '0.5rem 0.75rem', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '700', fontSize: '0.8125rem', color: '#64748b' },
  itemTd: { padding: '0.5rem 0.75rem', borderBottom: '1px solid #f1f5f9' },
  pinBadge: { background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '600' },
  totalesRow: { display: 'flex', justifyContent: 'flex-end', gap: '1.5rem', marginTop: '1rem', padding: '0.75rem 0 0', borderTop: '1px solid #e2e8f0', fontSize: '0.9rem' },
  totalFinal: { fontWeight: '700', color: '#1e40af', fontSize: '1rem' },
  comprobanteRow: { padding: '0.5rem 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.875rem' },
}

const envSt = {
  block: { border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem', background: 'white' },
  blockActive: { border: '2px solid #3b82f6', borderRadius: '8px', padding: '0.75rem', background: '#eff6ff' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' },
  checkIcon: { fontSize: '1.25rem', flexShrink: 0 },
  hint: { display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: '400' },
  subForm: { marginTop: '0.75rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #dbeafe' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: '600' },
  infoBox: { background: '#fef3c7', color: '#92400e', padding: '0.625rem 0.75rem', borderRadius: '6px', fontSize: '0.8125rem', fontWeight: '600' },
  pagoFila: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '0.625rem 0.75rem' },
  totalesRecibo: { marginTop: '0.75rem', padding: '0.625rem 0.75rem', background: '#f1f5f9', borderRadius: '6px', borderTop: '2px solid #1e293b' },
}

/**
 * Banner de artículos sin código Presea con edición INLINE: el código se carga
 * acá mismo y se guarda en el maestro (PUT /articulos/{id}) — no hace falta ir
 * a /articulos ni volver a cargarlo nunca más (pedido de Martin, 7 jun 2026).
 */
function BannerSinCodigo({ items, onPendientes }) {
  const [valores, setValores] = useState({})  // articulo_id → texto del input
  const [estados, setEstados] = useState({})  // articulo_id → 'guardando' | 'ok' | mensaje de error
  const [lupaPara, setLupaPara] = useState(null) // artículo pendiente cuya lupa 🔍 está abierta

  const [alias, setAlias] = useState({})      // articulo_id → alias Presea al que quedó vinculado

  // Artículos únicos sin resolver (un mismo artículo puede estar en 2 renglones).
  // "Resuelto" = tiene codigo_presea propio o vínculo factura_como.
  const articulos = []
  const vistos = new Set()
  for (const it of items) {
    const a = it.articulo
    if (a && !a.codigo_presea && !a.factura_como_id && !vistos.has(a.id)) { vistos.add(a.id); articulos.push(a) }
  }
  if (articulos.length === 0) return null

  const pendientes = articulos.filter(a => estados[a.id] !== 'ok')

  const guardar = async (a, codigoElegido = null) => {
    const codigo = (codigoElegido ?? valores[a.id] ?? '').trim()
    if (!codigo) return
    setEstados(e => ({ ...e, [a.id]: 'guardando' }))
    try {
      // Vincula contra un artículo EXISTENTE del maestro (código o alias) —
      // regla: el código tiene que existir en Presea. A Presea viaja el alias
      // del vinculado, y los reintentos reconstruyen el payload solos.
      const { data } = await api.post(`/articulos/${a.id}/vincular-presea`, { codigo })
      a.factura_como_id = data.factura_como_id
      setAlias(v => ({ ...v, [a.id]: data.factura_como?.codigo_alfa || codigo }))
      setEstados(e => {
        const nuevos = { ...e, [a.id]: 'ok' }
        onPendientes?.(articulos.filter(x => nuevos[x.id] !== 'ok').length)
        return nuevos
      })
    } catch (err) {
      setEstados(e => ({ ...e, [a.id]: err.response?.data?.message || 'No se pudo vincular' }))
    }
  }

  return (
    <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
      <strong>⚠ {pendientes.length > 0
        ? `${pendientes.length} artículo${pendientes.length !== 1 ? 's' : ''} sin código Presea`
        : 'Códigos completados ✓'}</strong>
      {pendientes.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#78350f' }}>
          <strong>La NV no se puede enviar a Presea sin estos códigos.</strong> Buscá el
          artículo del maestro (por nombre o código) y vinculalo: queda guardado y no se
          pide más. El envío usa el dato nuevo automáticamente.
        </div>
      )}
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {articulos.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontSize: 12, color: '#78350f', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={a.descripcion}>
              {a.codigo_alfa} — {a.descripcion}
            </span>
            {estados[a.id] === 'ok' ? (
              <span style={{ fontSize: 12, fontWeight: 700, color: '#166534', whiteSpace: 'nowrap' }}>✓ → {alias[a.id]}</span>
            ) : (
              <>
                <input value={valores[a.id] || ''} onChange={e => setValores(v => ({ ...v, [a.id]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && guardar(a)}
                  placeholder="Cód. o alias Presea" style={{ width: 130, padding: '4px 8px', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace' }} />
                <Boton variante="secondary" sm type="button" title="Buscar artículo del maestro"
                  onClick={() => setLupaPara(a)}>🔍</Boton>
                <Boton variante="secondary" sm disabled={estados[a.id] === 'guardando' || !(valores[a.id] || '').trim()}
                  onClick={() => guardar(a)}>
                  {estados[a.id] === 'guardando' ? '…' : '🔗 Vincular'}
                </Boton>
              </>
            )}
            {estados[a.id] && !['ok', 'guardando'].includes(estados[a.id]) && (
              <span style={{ fontSize: 11, color: '#b91c1c' }}>{estados[a.id]}</span>
            )}
          </div>
        ))}
      </div>

      {/* Lupa 🔍 — el mismo buscador de artículos de toda la app (F2), filtrado
          a los que SÍ tienen código Presea (no se puede vincular contra otro pendiente) */}
      {lupaPara && (
        <SelectorArticulosModal
          modo="simple"
          filtro={art => art.codigo_presea && art.id !== lupaPara.id}
          onSelect={art => guardar(lupaPara, art.codigo_presea)}
          onClose={() => setLupaPara(null)}
        />
      )}
    </div>
  )
}
